// TaskModal.jsx — versão desktop mantida + otimizações mobile-first (arquivo completo)
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Modal,
  Button,
  TextInput,
  Textarea,
  Select,
  Avatar,
  Label,
} from "flowbite-react";
import ReactDOM from "react-dom";
import {
  HiOutlinePencil,
  HiTrash as HiOutlineTrash,
  HiPhone,
  HiVideoCamera,
  HiMail,
  HiUserGroup,
  HiRefresh,
  HiChatAlt2,
  HiAnnotation,
  HiCurrencyDollar,
  HiOutlineX,
  HiMicrophone,
} from "react-icons/hi";
import { GrDocumentPpt } from "react-icons/gr";
import axios from "../../config/axiosInstance";
import { ADMIN, API, CRM, LEADS, USERS } from "../../config/apiConfig";
import StageTimeline from "../tables/StageTimeline";
import AutoCompleteInput from "../inputs/Autocomplete";
import TagPicker from "./TagPicker";
import ReceitaTooltip from "./ReceitaTooltip";
import LeadContactsPanel from "../crm/LeadContactsPanel";
import AttachmentPicker from "../inputs/AttachmentPicker";
import TaskEditModal from "./TaskEditModal";
import TiptapEditor from "../inputs/TipTapEditor";
import { useQuery, useQueryClient } from "react-query";
import {
  fmt,
  formatAnswer,
  hasHtmlTags,
  escapeHtml,
  toRichHtml,
  stripVisualEmpty,
  fmtBR,
  buildLocalISO,
  prettySize,
  fileIconFor,
  invalidateStats,
  dateToHHMM,
  labelIcon,
  labelMap,
  formatDocument,
} from "../../utils/utils";
import TaskItem from "../crm/TaskItem";
import TaskListCompact from "../crm/TaskListCompact";
import { MdOutlineModeEdit } from "react-icons/md";
import { FaRegTrashCan } from "react-icons/fa6";
import InstitutionsMultiSelect from "../inputs/InstitutionsMultiSelect";

const extractResponsibleIds = (entity) => {
  const ids = [];
  const arr =
    entity?.responsibles ||
    entity?.responsibleUsers ||
    entity?.taskResponsibles ||
    entity?.TaskResponsibles;
  if (Array.isArray(arr)) {
    arr.forEach((r) => {
      const rid = r?.id || r?.userId || r?.user_id || r?.UserId;
      if (rid) ids.push(String(rid));
    });
  }
  const single =
    entity?.responsibleId || entity?.responsible?.id || entity?.responsible_id;
  if (single) ids.push(String(single));
  return Array.from(new Set(ids.filter(Boolean)));
};

function formatCurrencyValue(value) {
  if (value === undefined || value === null) return "";
  const str = String(value).trim();
  if (!str) return "";

  let sanitized = str.replace(/R\$/gi, "").replace(/\s/g, "");
  if (sanitized.includes(",")) {
    sanitized = sanitized.replace(/\./g, "").replace(",", ".");
  } else {
    sanitized = sanitized.replace(/[^\d.]/g, "");
  }
  if (!sanitized) return "";

  const number = Number(sanitized);
  if (Number.isNaN(number)) return "";

  return number.toLocaleString("pt-BR", {
    style: "currency",
    currency: "BRL",
  });
}

function parseCurrencyNumber(value) {
  if (!value && value !== 0) return null;
  const str = String(value).trim();
  if (!str) return null;

  const cleaned = str.replace(/[^0-9.,-]/g, "");
  const hasDigits = cleaned.replace(/[-]/g, "");
  if (!hasDigits) return null;

  const isNegative = cleaned.trim().startsWith("-");
  const unsigned = hasDigits;
  const hasComma = unsigned.includes(",");
  const hasDot = unsigned.includes(".");
  const lastComma = unsigned.lastIndexOf(",");
  const lastDot = unsigned.lastIndexOf(".");

  let normalized = unsigned;

  if (hasComma && hasDot) {
    if (lastComma > lastDot) {
      normalized =
        unsigned.slice(0, lastComma).replace(/[.]/g, "") +
        "." +
        unsigned.slice(lastComma + 1);
    } else {
      normalized =
        unsigned.slice(0, lastDot).replace(/[,]/g, "") +
        "." +
        unsigned.slice(lastDot + 1);
    }
  } else if (hasComma) {
    normalized =
      unsigned.slice(0, lastComma).replace(/[.]/g, "") +
      "." +
      unsigned.slice(lastComma + 1);
  } else if (hasDot) {
    const decimals = unsigned.length - lastDot - 1;
    if (decimals > 0 && decimals <= 2) {
      normalized =
        unsigned.slice(0, lastDot).replace(/[,]/g, "") +
        "." +
        unsigned.slice(lastDot + 1);
    } else {
      normalized = unsigned.replace(/[.]/g, "");
    }
  }

  const signedValue = `${isNegative ? "-" : ""}${normalized}`;
  const number = Number(signedValue);
  return Number.isFinite(number) ? number : null;
}

function formatCurrencyMask(value) {
  if (value === undefined || value === null) return "";
  const digits = (value.match(/\d/g) || []).join("");
  if (!digits) return "";

  const number = Number(digits) / 100;
  if (Number.isNaN(number)) return "";

  return number.toLocaleString("pt-BR", {
    style: "currency",
    currency: "BRL",
  });
}

function formatDateShort(value) {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleDateString("pt-BR");
}

const DEFAULT_ADDITIONAL_INFO = {
  city: "",
  revenue: "",
  indebtedness: "",
  creditNeed: "",
  hasGuarantee: false,
  guaranteeDetails: "",
  guarantee: "",
  institutionalPresentation: "",
  others: "",
  presumedRevenue: "",
  state: "",
};

const DEFAULT_REJECTION_REASONS = [
  "Reprovado por ausência de garantia",
  "Reprovado por baixo faturamento",
  "Reprovado por excesso de restritivos",
];

const LEAD_SOURCE_LABELS = {
  manual: "Manual",
  website: "Site",
  whatsapp: "WhatsApp",
  instagram: "Instagram",
  facebook: "Facebook",
  google_ads: "Google Ads",
  organic: "Orgânico",
  referral: "Indicação",
  event: "Evento",
  import: "Importação",
};

const resolveLeadSource = (lead) =>
  lead?.source ||
  lead?.leadSource ||
  lead?.lead_source ||
  lead?.utm?.source ||
  lead?.utm?.utm_source ||
  null;

const formatLeadSourceLabel = (value) => {
  if (!value) return "—";
  const normalized = String(value).toLowerCase();
  return LEAD_SOURCE_LABELS[normalized] || value;
};

const normalizeCardTags = (value) => {
  const list = Array.isArray(value) ? value : [];
  const seen = new Set();

  return list
    .map((tag) => ({
      id: tag?.id,
      name: tag?.name,
      color: tag?.color,
    }))
    .filter((tag) => tag.id && tag.name)
    .filter((tag) => {
      const key = String(tag.id);
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
};

const getInitialCardTags = (task) => {
  const fromAdditional = normalizeCardTags(task?.additionalInformation?.cardTags);
  const fromTaskTags = normalizeCardTags(task?.tags);
  const fromPrimary = normalizeCardTags(task?.tag ? [task.tag] : []);
  return normalizeCardTags([...fromAdditional, ...fromTaskTags, ...fromPrimary]);
};

function normalizeAdditionalInfo(value) {
  if (!value) return { ...DEFAULT_ADDITIONAL_INFO };
  let parsed = value;
  if (typeof value === "string") {
    try {
      parsed = JSON.parse(value);
    } catch (err) {
      console.warn("Falha ao parsear informações adicionais:", err);
      parsed = {};
    }
  }
  if (typeof parsed !== "object" || parsed === null) {
    parsed = {};
  }

  const hasGuaranteeParsed =
    parsed.hasGuarantee === true ||
    parsed.hasGuarantee === "true" ||
    parsed.hasGuarantee === 1 ||
    parsed.hasGuarantee === "1";
  const legacyGuarantee =
    typeof parsed.guarantee === "string" ? parsed.guarantee.trim() : "";
  const guaranteeDetails =
    (parsed.guaranteeDetails || "").toString().trim() || legacyGuarantee;

  const normalized = {
    ...DEFAULT_ADDITIONAL_INFO,
    ...parsed,
    hasGuarantee:
      typeof parsed.hasGuarantee !== "undefined"
        ? hasGuaranteeParsed
        : Boolean(guaranteeDetails),
    guaranteeDetails,
    guarantee: legacyGuarantee,
  };

  normalized.revenue = formatCurrencyValue(normalized.revenue);
  normalized.presumedRevenue = formatCurrencyValue(normalized.presumedRevenue);
  normalized.state = String(normalized.state || "").toUpperCase();

  return normalized;
}

const BRAZILIAN_STATES = [
  { value: "AC", label: "Acre" },
  { value: "AL", label: "Alagoas" },
  { value: "AP", label: "Amapá" },
  { value: "AM", label: "Amazonas" },
  { value: "BA", label: "Bahia" },
  { value: "CE", label: "Ceará" },
  { value: "DF", label: "Distrito Federal" },
  { value: "ES", label: "Espírito Santo" },
  { value: "GO", label: "Goiás" },
  { value: "MA", label: "Maranhão" },
  { value: "MT", label: "Mato Grosso" },
  { value: "MS", label: "Mato Grosso do Sul" },
  { value: "MG", label: "Minas Gerais" },
  { value: "PA", label: "Pará" },
  { value: "PB", label: "Paraíba" },
  { value: "PR", label: "Paraná" },
  { value: "PE", label: "Pernambuco" },
  { value: "PI", label: "Piauí" },
  { value: "RJ", label: "Rio de Janeiro" },
  { value: "RN", label: "Rio Grande do Norte" },
  { value: "RS", label: "Rio Grande do Sul" },
  { value: "RO", label: "Rondônia" },
  { value: "RR", label: "Roraima" },
  { value: "SC", label: "Santa Catarina" },
  { value: "SP", label: "São Paulo" },
  { value: "SE", label: "Sergipe" },
  { value: "TO", label: "Tocantins" },
];

const getStateLabel = (value) => {
  if (!value) return "";
  const state = BRAZILIAN_STATES.find((item) => item.value === value);
  return state ? state.label : value;
};

function CollapsibleSection({ title, subtitle, defaultOpen = true, children }) {
  const [open, setOpen] = React.useState(defaultOpen);
  const sectionId = React.useId();

  return (
    <div className="rounded-lg border border-gray-200 bg-white">
      <div className="px-3 py-2 border-b border-gray-200 flex items-center justify-between">
        <div className="text-sm font-semibold text-gray-800">
          {title}{" "}
          {subtitle ? (
            <span className="ml-2 text-xs text-gray-500">{subtitle}</span>
          ) : null}
        </div>

        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          aria-expanded={open}
          aria-controls={sectionId}
          className="inline-flex items-center gap-1.5 text-xs text-gray-600 hover:text-gray-800 px-2 py-1 rounded-md hover:bg-gray-50"
          title={open ? "Recolher" : "Expandir"}
        >
          <span>{open ? "Recolher" : "Expandir"}</span>
          <svg
            className={`h-4 w-4 transition-transform ${
              open ? "rotate-180" : ""
            }`}
            viewBox="0 0 20 20"
            fill="currentColor"
          >
            <path
              fillRule="evenodd"
              d="M5.23 7.21a.75.75 0 011.06.02L10 10.18l3.71-2.95a.75.75 0 01.94 1.16l-4.24 3.37a.75.75 0 01-.94 0L5.21 8.39a.75.75 0 01.02-1.18z"
              clipRule="evenodd"
            />
          </svg>
        </button>
      </div>

      <div
        id={sectionId}
        className={`transition-all ${
          open ? "max-h-[1200px] opacity-100" : "max-h-0 opacity-0"
        } overflow-hidden`}
      >
        <div className="p-3">{children}</div>
      </div>
    </div>
  );
}

const ApprovalValuesPanel = React.memo(function ApprovalValuesPanel({
  className = "",
  approvalRows = [],
  loadingApprovals = false,
  banks = [],
  rejectionReasons = [],
  approvalBankId = "",
  approvalAmountInput = "",
  approvalDecisionDate = "",
  approvalApproved = true,
  approvalNotes = "",
  editingApprovalId = null,
  onChangeApprovalBankId = () => {},
  onChangeApprovalAmount = () => {},
  onChangeApprovalDecisionDate = () => {},
  onChangeApprovalApproved = () => {},
  onChangeApprovalNotes = () => {},
  onSelectRejectionReason = () => {},
  onEditApproval = () => {},
  onCancelEditApproval = () => {},
  handleSaveApproval = () => {},
  savingApproval = false,
}) {
  return (
    <div
      className={`mt-4 rounded-lg border border-emerald-400/80 bg-green-50 p-4 shadow-inner ${className}`}
    >
      <div className="flex items-center justify-between">
        <div>
          <h5 className="text-sm font-semibold">
            Valores aprovados / Reprovados
          </h5>
          <p className="text-[11px] ">
            Baseado nas instituições vinculadas a este card
          </p>
        </div>
        <span className="text-[11px] uppercase tracking-[0.3em] text-white/60">
          {approvalRows.length} registro
          {approvalRows.length === 1 ? "" : "s"}
        </span>
      </div>
      <div className="mt-3 space-y-2 max-h-64 overflow-y-auto pr-1">
        {loadingApprovals ? (
          <p className="text-[11px]">
            Carregando valores aprovados/ reprovados…
          </p>
        ) : approvalRows.length ? (
          approvalRows.slice(0, 4).map((approval) => (
            <div
              key={approval.id}
              className={`rounded-2xl border border-white/20 bg-black/10 p-3 ${
                editingApprovalId === approval.id
                  ? "border-2 border-green-400/70"
                  : ""
              }`}
            >
              <div className="flex items-center justify-between text-[11px] uppercase tracking-[0.3em]">
                <span>{approval.bank?.name || "Instituição"}</span>
                <div className="flex items-center gap-2">
                  <span>{formatDateShort(approval.decisionAt)}</span>
                  <button
                    type="button"
                    className="text-emerald-700 hover:text-emerald-900"
                    onClick={() => onEditApproval(approval)}
                    title="Editar valor"
                  >
                    <HiOutlinePencil size={14} />
                  </button>
                </div>
              </div>
              <p className="text-sm font-semibold mt-1">
                {approval.approved ? "Aprovado" : "Reprovado"} ·{" "}
                {Number(approval.amount || 0).toLocaleString("pt-BR", {
                  style: "currency",
                  currency: "BRL",
                })}
              </p>
              {approval.notes ? (
                <p className="mt-1 text-[11px] ">{approval.notes}</p>
              ) : null}
            </div>
          ))
        ) : (
          <p className="text-[11px] ">
            Ainda não há valores aprovados registrados.
          </p>
        )}
        {approvalRows.length > 4 && (
          <p className="text-[11px] ">
            Mostrando 4 de {approvalRows.length} registros
          </p>
        )}
      </div>
      <div className="mt-4 border-t border-white/30 pt-3">
        <p className="text-[11px] uppercase tracking-[0.3em] ">
          {editingApprovalId ? "Editar valor" : "Registrar novo valor"}
        </p>
        <div className="mt-3 grid gap-2">
          <div className="flex flex-col gap-1">
            <label className="text-[11px] ">Instituição</label>
            <Select
              value={approvalBankId}
              onChange={onChangeApprovalBankId}
              disabled={!banks.length}
              className="bg-white text-gray-900"
            >
              <option value="">
                {banks.length ? "Selecione" : "Carregando..."}
              </option>
              {banks.map((bank) => (
                <option key={bank.id} value={bank.id}>
                  {bank.name}
                </option>
              ))}
            </Select>
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-[11px] text-gray-800">Valor aprovado</label>
            <TextInput
              value={approvalAmountInput}
              onChange={onChangeApprovalAmount}
              placeholder="R$ 0,00"
              className="bg-white text-gray-900"
            />
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-[11px] text-gray-800">Data da decisão</label>
            <TextInput
              type="date"
              value={approvalDecisionDate}
              onChange={onChangeApprovalDecisionDate}
              className="bg-white text-gray-900"
            />
          </div>
        </div>
        <div className="mt-3 grid gap-2">
          <div className="flex flex-col gap-1">
            <label className="text-[11px] text-gray-800">Status</label>
            <Select
              value={approvalApproved ? "approved" : "rejected"}
              onChange={onChangeApprovalApproved}
              className="bg-white text-gray-900"
            >
              <option value="approved">Aprovado</option>
              <option value="rejected">Reprovado</option>
            </Select>
            {!approvalApproved && rejectionReasons.length ? (
              <div className="mt-2">
                <p className="text-[11px] text-gray-500">Motivos rápidos</p>
                <div className="mt-1 flex flex-wrap gap-2">
                  {rejectionReasons.map((reason) => (
                    <button
                      key={reason}
                      type="button"
                      onClick={() => onSelectRejectionReason(reason)}
                      className="rounded-full border border-gray-200 bg-white px-3 py-1 text-[11px] text-gray-700 hover:border-gray-300 hover:bg-gray-50"
                    >
                      {reason}
                    </button>
                  ))}
                </div>
              </div>
            ) : null}
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-[11px] text-gray-800">Observações</label>
            <Textarea
              value={approvalNotes}
              onChange={onChangeApprovalNotes}
              rows={2}
              className="bg-white text-gray-900"
            />
          </div>
        </div>
        <div className="mt-3 flex justify-end">
          {editingApprovalId && (
            <Button
              size="sm"
              color="light"
              onClick={onCancelEditApproval}
              disabled={savingApproval}
              className="mr-2"
            >
              Cancelar
            </Button>
          )}
          <Button
            size="sm"
            color="light"
            onClick={handleSaveApproval}
            disabled={savingApproval}
          >
            {savingApproval
              ? "Salvando…"
              : editingApprovalId
              ? "Salvar edição"
              : "Salvar"}
          </Button>
        </div>
      </div>
    </div>
  );
});

export default function TaskModal({
  task,
  onClose,
  updateTask,
  history,
  setHistory,
  setSelectedTask,
  stages,
  onMoveStage,
  onRefetchCard,
  onCardDeleted,
}) {
  const leadId = task?.lead?.id || null;
  const qc = useQueryClient();
  const [title, setTitle] = useState(task?.title || "Visualizar card");
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [isUploadingTask, setIsUploadingTask] = useState(false);
  const [uploadPct, setUploadPct] = useState(0);

  const [actionFiles, setActionFiles] = useState([]);
  const [editFiles, setEditFiles] = useState([]);
  const [isEditingTask, setIsEditingTask] = useState(false);

  const [confirmCompleteTask, setConfirmCompleteTask] = useState(null); // null | task

  const openConfirmComplete = (t) => setConfirmCompleteTask(t);
  const closeConfirmComplete = () => setConfirmCompleteTask(null);

  const confirmCompleteNow = async () => {
    const t = confirmCompleteTask;
    if (!t) return;
    await handleToggleTaskStatus(t, true);
    closeConfirmComplete();
  };

  const [editModalOpen, setEditModalOpen] = useState(false);
  const [taskBeingEdited, setTaskBeingEdited] = useState(null);

  // comentários com HTML (Tiptap)
  const [commentHtml, setCommentHtml] = useState("");
  const [commentRich, setCommentRich] = useState(false); // mobile: alternar textarea/Tiptap

  // speech-to-text (descrição da tarefa e nova observação)
  const mediaRecorderRef = useRef(null);
  const mediaStreamRef = useRef(null);
  const audioChunksRef = useRef([]);
  const dictationAbortRef = useRef(false);
  const [dictationTarget, setDictationTarget] = useState(null); // "action" | "comment"
  const [dictationLoadingTarget, setDictationLoadingTarget] = useState(null);
  const [dictationTranscribingTarget, setDictationTranscribingTarget] =
    useState(null);
  const dictationTimerRef = useRef(null);
  const dictationSupported =
    typeof window !== "undefined" &&
    navigator?.mediaDevices?.getUserMedia &&
    window.MediaRecorder;

  const appendTranscript = useCallback((prev, transcript) => {
    const clean = String(transcript || "").trim();
    if (!clean) return prev || "";
    const current = String(prev || "");
    if (!current) return toRichHtml(clean);
    if (hasHtmlTags(current)) return `${current}${toRichHtml(clean)}`;
    const sep = current.endsWith("\n") ? "" : "\n";
    return `${current}${sep}${clean}`;
  }, []);

  const normalizeMimeType = useCallback((value = "") => {
    return String(value || "").split(";")[0].trim();
  }, []);

  const extFromMime = useCallback(
    (value = "") => {
      const type = normalizeMimeType(value).toLowerCase();
      if (!type) return "webm";
      if (type.includes("webm")) return "webm";
      if (type.includes("ogg") || type.includes("oga")) return "ogg";
      if (type.includes("mp4")) return "mp4";
      if (type.includes("mpeg") || type.includes("mp3")) return "mp3";
      if (type.includes("wav")) return "wav";
      if (type.includes("flac")) return "flac";
      if (type.includes("m4a")) return "m4a";
      return "webm";
    },
    [normalizeMimeType]
  );

  const applyTranscript = useCallback(
    (target, transcript) => {
      if (!transcript) return;
      if (target === "comment") {
        setCommentHtml((prev) => appendTranscript(prev, transcript));
      }
      if (target === "action") {
        setActionNoteHtml((prev) => appendTranscript(prev, transcript));
      }
    },
    [appendTranscript]
  );

  const clearDictationTimer = useCallback(() => {
    if (dictationTimerRef.current) {
      clearTimeout(dictationTimerRef.current);
      dictationTimerRef.current = null;
    }
    setDictationLoadingTarget(null);
  }, []);

  const cleanupStream = useCallback(() => {
    if (mediaStreamRef.current) {
      mediaStreamRef.current.getTracks().forEach((track) => track.stop());
    }
    mediaStreamRef.current = null;
  }, []);

  const stopDictation = useCallback(
    (options = {}) => {
      const { discard = false } = options;
      clearDictationTimer();
      if (discard) dictationAbortRef.current = true;
      const recorder = mediaRecorderRef.current;
      if (recorder && recorder.state !== "inactive") {
        try {
          recorder.stop();
        } catch (err) {
          console.warn("Falha ao parar a gravação:", err);
        }
      } else {
        dictationAbortRef.current = false;
        cleanupStream();
        setDictationTarget(null);
      }
    },
    [clearDictationTimer, cleanupStream]
  );

  const pickMimeType = useCallback(() => {
    if (typeof window === "undefined" || !window.MediaRecorder) return "";
    const types = [
      "audio/webm;codecs=opus",
      "audio/webm",
      "audio/ogg;codecs=opus",
      "audio/mp4",
    ];
    return types.find((t) => MediaRecorder.isTypeSupported(t)) || "";
  }, []);

  const transcribeAudio = useCallback(
    async (target, blob) => {
      setDictationTranscribingTarget(target);
      try {
        const fd = new FormData();
        const safeMime = normalizeMimeType(blob.type);
        const ext = extFromMime(safeMime);
        fd.append("file", blob, `audio.${ext}`);
        const res = await axios.post(API.TRANSCRIBE, fd, {
          headers: { "Content-Type": undefined },
        });
        const text = res?.data?.text || res?.data?.data?.text || "";
        if (!text) {
          alert("Não foi possível transcrever o áudio.");
          return;
        }
        applyTranscript(target, text);
      } catch (err) {
        console.error("Erro ao transcrever áudio:", err);
        alert(
          err?.response?.data?.message || "Falha ao transcrever áudio."
        );
      } finally {
        setDictationTranscribingTarget(null);
      }
    },
    [applyTranscript, extFromMime, normalizeMimeType]
  );

  const startDictation = useCallback(
    async (target) => {
      if (!dictationSupported) {
        alert(
          "Seu navegador não suporta gravação de áudio. Use Chrome ou Edge."
        );
        return;
      }
      if (mediaRecorderRef.current) stopDictation({ discard: true });

      let stream;
      try {
        stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      } catch (err) {
        console.warn("Erro ao acessar microfone:", err);
        alert("Permissão de microfone negada.");
        return;
      }

      mediaStreamRef.current = stream;
      audioChunksRef.current = [];
      const mimeType = pickMimeType();
      const recorder = new MediaRecorder(
        stream,
        mimeType ? { mimeType } : undefined
      );

      recorder.ondataavailable = (event) => {
        if (event.data && event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      recorder.onerror = (event) => {
        console.warn("Erro na gravação de áudio:", event);
      };

      recorder.onstop = async () => {
        const discard = dictationAbortRef.current;
        dictationAbortRef.current = false;
        const chunks = audioChunksRef.current;
        audioChunksRef.current = [];
        cleanupStream();
        if (mediaRecorderRef.current === recorder) {
          mediaRecorderRef.current = null;
        }
        setDictationTarget(null);
        if (discard || !chunks.length) return;
        const safeMime = normalizeMimeType(
          recorder.mimeType || "audio/webm"
        );
        const blob = new Blob(chunks, {
          type: safeMime || "audio/webm",
        });
        await transcribeAudio(target, blob);
      };

      mediaRecorderRef.current = recorder;
      setDictationTarget(target);
      recorder.start();
    },
    [
      cleanupStream,
      dictationSupported,
      pickMimeType,
      stopDictation,
      transcribeAudio,
    ]
  );

  const startDictationWithDelay = useCallback(
    (target) => {
      if (dictationLoadingTarget || dictationTranscribingTarget) return;
      clearDictationTimer();
      if (mediaRecorderRef.current) stopDictation({ discard: true });
      setDictationLoadingTarget(target);
      dictationTimerRef.current = setTimeout(() => {
        setDictationLoadingTarget(null);
        startDictation(target);
      }, 1500);
    },
    [
      clearDictationTimer,
      dictationLoadingTarget,
      dictationTranscribingTarget,
      startDictation,
      stopDictation,
    ]
  );

  useEffect(() => () => stopDictation({ discard: true }), [stopDictation]);

  const DictationButton = ({ target, className = "" }) => {
    const active = dictationTarget === target;
    const loading = dictationLoadingTarget === target;
    const transcribing = dictationTranscribingTarget === target;
    return (
      <button
        type="button"
        onClick={
          active ? () => stopDictation() : () => startDictationWithDelay(target)
        }
        disabled={!dictationSupported || loading || transcribing}
        title={
          dictationSupported
            ? active
              ? "Parar gravação"
              : loading
              ? "Preparando gravação"
              : transcribing
              ? "Transcrevendo áudio"
              : "Voz -> Texto"
            : "Gravação de áudio indisponível"
        }
        className={`inline-flex items-center gap-1 rounded-md border px-2 py-1 text-xs ${
          active
            ? "border-red-300 bg-red-50 text-red-700"
            : "border-gray-300 bg-white text-gray-700 hover:bg-gray-50"
        } ${!dictationSupported ? "opacity-60 cursor-not-allowed" : ""} ${className}`}
      >
        <HiMicrophone size={14} />
        {loading
          ? "Carregando…"
          : transcribing
          ? "Transcrevendo…"
          : active
          ? "Parar"
          : "Voz -> Texto"}
      </button>
    );
  };

  // responsável do CARD
  const [isEditingResponsible, setIsEditingResponsible] = useState(false);
  const [availableUsers, setAvailableUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [selectedResponsible, setSelectedResponsible] = useState(
    task?.responsible?.id || ""
  );

  // cliente do CARD
  const [isEditingClient, setIsEditingClient] = useState(false);
  const [availableClients, setAvailableClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState(task?.lead?.id || "");

  // 🔧 Campos editáveis do cliente (inicializam com o cliente atual)
  const customer = task?.lead || {};
  const customerEmail = customer?.email || customer?.emails?.[0] || "";
  const customerPhone = customer?.phoneNumber || "";
  const customerDocument = customer?.document || "";
  const customerResponsible = customer?.responsibleName || customer?.name || "";

  const [clientName, setClientName] = useState(customer?.name || "");
  const [clientEmail, setClientEmail] = useState(customerEmail || "");
  const [clientPhone, setClientPhone] = useState(customerPhone || "");
  const [clientDocument, setClientDocument] = useState(customerDocument || "");
  const [clientResponsible, setClientResponsible] = useState(
    customerResponsible || ""
  );
  const [savingClient, setSavingClient] = useState(false);
  const [additionalInfo, setAdditionalInfo] = useState(() =>
    normalizeAdditionalInfo(task?.additionalInformation)
  );
  const [savingAdditionalInfo, setSavingAdditionalInfo] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);

  // edição/exclusão de comentários
  const [editingCommentId, setEditingCommentId] = useState(null);
  const [editCommentHtml, setEditCommentHtml] = useState("");

  // criação de PRÓXIMA AÇÃO
  const [actionType, setActionType] = useState(task?.nextActionType || "");
  const [actionNoteHtml, setActionNoteHtml] = useState(
    task?.nextActionNote || ""
  );
  const [actionDueDate, setActionDueDate] = useState(
    task?.nextActionDueDate ? String(task.nextActionDueDate).slice(0, 10) : ""
  );
  const [actionDueTime, setActionDueTime] = useState(
    task?.nextActionDueDate ? dateToHHMM(new Date(task.nextActionDueDate)) : ""
  );
  const [actionPhone, setActionPhone] = useState("");
  const [actionResponsibleIds, setActionResponsibleIds] = useState(
    extractResponsibleIds(task)
  );
  const [showRichAction, setShowRichAction] = useState(false); // mobile: textarea -> editor rico

  // edição inline de tarefas existentes
  const [editingTaskId, setEditingTaskId] = useState(null);
  const [editType, setEditType] = useState("");
  const [editNoteHtml, setEditNoteHtml] = useState("");
  const [editDueDate, setEditDueDate] = useState("");
  const [editDueTime, setEditDueTime] = useState("");
  const [editResponsibleIds, setEditResponsibleIds] = useState([]);

  const [tasks, setTasks] = useState([]);
  const [loadingTasks, setLoadingTasks] = useState(false);

  // TAGS do card
  const [currentTags, setCurrentTags] = useState(() => getInitialCardTags(task));
  const currentTag = currentTags[0] || null;
  const [showTagPicker, setShowTagPicker] = useState(false);
  const companyIdForTags = task?.lead?.id || task?.companyId || null;
  const [deleting, setDeleting] = useState(false);

  // Mobile State
  const [mobileTab, setMobileTab] = useState("tarefas"); // 'detalhes'|'tarefas'|'comentarios'|'acao'

  const [showReceita, setShowReceita] = useState(false);
  const [receitaAnchor, setReceitaAnchor] = useState(null);

  const handleDeleteCard = async () => {
    if (!task?.id) return;
    const ok = window.confirm(
      "Excluir este card? Todas as tarefas e histórico ligados a ele serão removidos."
    );
    if (!ok) return;
    try {
      setDeleting(true);
      await axios.delete(`${CRM.DELETE_CARD}/${task.id}`);

      if (typeof onCardDeleted === "function") {
        onCardDeleted(task.id);
      }

      onClose?.();
    } catch (e) {
      console.error("Erro ao excluir card:", e);
      alert("Não foi possível excluir o card.");
    } finally {
      setDeleting(false);
    }
  };

  const handleClose = async () => {
    try {
      await onRefetchCard?.(task?.id);
    } finally {
      onClose?.();
    }
  };

  const handleAssignTags = async (tagsOrEmpty) => {
    const nextTags = normalizeCardTags(tagsOrEmpty || []);
    const primaryTag = nextTags[0] || null;

    const nextAdditionalInfo = {
      ...normalizeAdditionalInfo(task?.additionalInformation),
      cardTags: nextTags,
    };

    try {
      await axios.put(`${CRM.UPDATE_CARD}/${task?.id}`, {
        tagId: primaryTag ? primaryTag.id : null,
        additionalInformation: nextAdditionalInfo,
      });
      const updatedAt = new Date().toISOString();
      setCurrentTags(nextTags);
      setAdditionalInfo((prev) => ({ ...prev, cardTags: nextTags }));

      updateTask({
        ...task,
        tag: primaryTag,
        tags: nextTags,
        additionalInformation: nextAdditionalInfo,
        updatedAt,
      });
      setSelectedTask?.((prev) => ({
        ...prev,
        tag: primaryTag,
        tags: nextTags,
        additionalInformation: nextAdditionalInfo,
        updatedAt,
      }));
    } catch (e) {
      console.error("Erro ao atribuir tag ao card:", e);
      alert("Não foi possível atualizar a etiqueta do card.");
    }
  };

  useEffect(() => {
    setCurrentTags(getInitialCardTags(task));
  }, [task?.id, task?.tag, task?.tags, task?.additionalInformation]);

  const ACTION_PRESETS = [
    { key: "ligacao", label: "Ligação", icon: HiPhone },
    { key: "reuniao", label: "Reunião", icon: HiVideoCamera },
    { key: "email", label: "E-mail", icon: HiMail },
    { key: "visita", label: "Visita", icon: HiUserGroup },
    { key: "followup", label: "Follow-up", icon: HiRefresh },
    { key: "whatsapp", label: "WhatsApp", icon: HiChatAlt2 },
    { key: "proposta", label: "Proposta", icon: GrDocumentPpt },
  ];
  const defaultNoteFor = (k) => {
    switch (k) {
      case "ligacao":
        return "<p>Ligar para o cliente</p>";
      case "reuniao":
        return "<p>Agendar reunião com o cliente</p>";
      case "email":
        return "<p>Enviar e-mail para o cliente</p>";
      case "visita":
        return "<p>Agendar visita com o cliente</p>";
      case "followup":
        return "<p>Fazer follow-up</p>";
      case "whatsapp":
        return "<p>Enviar mensagem no WhatsApp</p>";
      case "proposta":
        return "<p>Enviar proposta para o cliente.</p>";
      default:
        return "<p>Definir próxima ação</p>";
    }
  };

  // ===== Fetch base =====
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        setLoadingUsers(true);
        const res = await axios.get(`${USERS.GET_USERS}?itemsPerPage=-1`);
        setAvailableUsers(res.data?.data?.data || []);
      } catch (err) {
        console.error("Erro ao buscar usuários:", err);
      } finally {
        setLoadingUsers(false);
      }
    };
    const fetchClients = async () => {
      try {
        const res = await axios.get(`${ADMIN.GET_CUSTOMERS}?itemsPerPage=-1`);
        setAvailableClients(res.data?.data?.data || []);
      } catch (err) {
        console.error("Erro ao buscar clientes:", err);
      }
    };
    const fetchMe = async () => {
      try {
        const r = await axios.get(USERS.GET_USER_CURRENT);
        setCurrentUser(r?.data?.data || r?.data?.user || null);
      } catch (e) {
        console.warn("Não foi possível obter o usuário logado (ME).", e);
      }
    };
    fetchUsers();
    fetchClients();
    fetchMe();
  }, []);

  const userOptions = useMemo(
    () =>
      (availableUsers || []).map((u) => ({
        value: u.id,
        label: `${u.name || "(sem nome)"}${u.email ? ` — ${u.email}` : ""}`,
      })),
    [availableUsers]
  );

  const getUserNameById = (id) => {
    if (!id) return "";
    const u = (availableUsers || []).find((x) => String(x.id) === String(id));
    return u?.name || "";
  };

  const reloadTasks = async () => {
    try {
      setLoadingTasks(true);
      const res = await axios.get(`${CRM.ADD_CARD_HISTORY}/${task?.id}/tasks`);
      const list = Array.isArray(res?.data?.data) ? res.data.data : [];
      list.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
      setTasks(list);
    } catch (err) {
      console.error("Erro ao buscar tarefas:", err);
    } finally {
      setLoadingTasks(false);
    }
  };
  useEffect(() => {
    if (task?.id) reloadTasks();
  }, [task?.id]);

  // 🔧 Re-sincroniza os campos do cliente quando trocar de card
  useEffect(() => {
    const c = task?.lead || {};
    const cEmail = c?.email || c?.emails?.[0] || "";
    setClientName(c?.name || "");
    setClientEmail(cEmail || "");
    setClientPhone(c?.phoneNumber || "");
    setClientDocument(c?.document || "");
    setClientResponsible(c?.responsibleName || c?.name || "");
    setSelectedClient(c?.id || "");
  }, [task?.id]);

  useEffect(() => {
    setActionResponsibleIds(extractResponsibleIds(task));
  }, [task?.id]);

  useEffect(() => {
    const normalized = normalizeAdditionalInfo(task?.additionalInformation);
    if (!normalized.presumedRevenue && task?.lead?.presumedRevenue != null) {
      normalized.presumedRevenue = formatCurrencyValue(
        task.lead.presumedRevenue
      );
    }
    if (!normalized.state && task?.lead?.state) {
      normalized.state = task.lead.state;
    }
    setAdditionalInfo(normalized);
  }, [
    task?.id,
    task?.additionalInformation,
    task?.lead?.presumedRevenue,
    task?.lead?.state,
  ]);

  // ===== Saves do CARD =====
  const handleSaveTitle = async () => {
    try {
      await axios.put(`${CRM.UPDATE_CARD}/${task?.id}`, { title });
      const updatedAt = new Date().toISOString();
      updateTask({ ...task, title, updatedAt });
      setSelectedTask?.((prev) => ({ ...prev, title, updatedAt }));
      setIsEditingTitle(false);
    } catch (err) {
      console.error("Erro ao salvar título:", err);
    }
  };

  const handleSaveResponsible = async (opt) => {
    try {
      const newId = opt?.value || "";
      setSelectedResponsible(newId);
      await axios.put(`${CRM.UPDATE_CARD}/${task?.id}`, {
        responsibleId: newId || null,
      });
      const userObj = availableUsers.find((u) => u.id == newId) || null;
      const updatedAt = new Date().toISOString();
      updateTask({ ...task, responsible: userObj, updatedAt });
      setSelectedTask?.((prev) => ({
        ...prev,
        responsible: userObj,
        updatedAt,
      }));
      setIsEditingResponsible(false);
    } catch (err) {
      console.error("Erro ao salvar responsável:", err);
    }
  };

  // 🔧 Salvar edição do cliente atual (PUT no cliente)
  const openClientEditor = () => {
    setMobileTab("detalhes");
    setIsEditingClient(true);
  };

  const handleSaveClientDetails = async () => {
    if (!task?.lead?.id) {
      alert("Cliente não identificado neste card.");
      return;
    }
    const presumedRevenueNumber = parseCurrencyNumber(
      additionalInfo.presumedRevenue
    );
    const payload = {
      name: clientName || undefined,
      email: clientEmail || undefined,
      phoneNumber: clientPhone || undefined,
      document: clientDocument || undefined,
      responsibleName: clientResponsible || undefined,
      state: additionalInfo.state || undefined,
      presumedRevenue: presumedRevenueNumber ?? undefined,
    };

    try {
      setSavingClient(true);
      await axios.put(`${LEADS.UPDATE(task.lead.id)}`, payload);
      const updatedLead = {
        ...(task.lead || {}),
        name: clientName,
        email: clientEmail,
        phoneNumber: clientPhone,
        document: clientDocument,
        responsibleName: clientResponsible,
        state: additionalInfo.state || undefined,
        presumedRevenue:
          parseCurrencyNumber(additionalInfo.presumedRevenue) ?? undefined,
      };
      const updatedAt = new Date().toISOString();

      updateTask({ ...task, lead: updatedLead, updatedAt });
      setSelectedTask?.((prev) => ({ ...prev, lead: updatedLead, updatedAt }));

      setIsEditingClient(false);
      await handleSaveAdditionalInfo();
    } catch (err) {
      console.error("Erro ao salvar dados do cliente:", err);
      alert("Não foi possível salvar os dados do cliente.");
    } finally {
      setSavingClient(false);
    }
  };

  const handleSaveAdditionalInfo = async () => {
    if (!task?.id) return;
    const formattedInfo = {
      ...additionalInfo,
      revenue: formatCurrencyValue(additionalInfo.revenue),
      presumedRevenue: formatCurrencyValue(additionalInfo.presumedRevenue),
    };
    try {
      setSavingAdditionalInfo(true);
      setAdditionalInfo(formattedInfo);
      await axios.put(`${CRM.UPDATE_CARD}/${task.id}`, {
        additionalInformation: formattedInfo,
      });
      const updatedAt = new Date().toISOString();
      const updatedTask = {
        ...task,
        additionalInformation: formattedInfo,
        updatedAt,
      };
      updateTask(updatedTask);
      setSelectedTask?.((prev) => ({
        ...prev,
        additionalInformation: formattedInfo,
        updatedAt,
      }));
    } catch (err) {
      console.error("Erro ao salvar informações adicionais:", err);
      alert("Não foi possível salvar as informações adicionais do card.");
    } finally {
      setSavingAdditionalInfo(false);
    }
  };

  const handleResetAdditionalInfo = () => {
    setAdditionalInfo(normalizeAdditionalInfo(task?.additionalInformation));
  };

  const updateAdditionalInfoField = (field, value) => {
    setAdditionalInfo((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleRevenueInputChange = (value) => {
    if (!value) {
      updateAdditionalInfoField("revenue", "");
      return;
    }
    const masked = formatCurrencyMask(value);
    updateAdditionalInfoField("revenue", masked);
  };

  const handlePresumedRevenueInputChange = (value) => {
    if (!value) {
      updateAdditionalInfoField("presumedRevenue", "");
      return;
    }
    const masked = formatCurrencyMask(value);
    updateAdditionalInfoField("presumedRevenue", masked);
  };

  const handleRevenueBlur = () => {
    setAdditionalInfo((prev) => ({
      ...prev,
      revenue: formatCurrencyValue(prev.revenue),
    }));
  };

  const handlePresumedRevenueBlur = () => {
    setAdditionalInfo((prev) => ({
      ...prev,
      presumedRevenue: formatCurrencyValue(prev.presumedRevenue),
    }));
  };

  const handleStateChange = (value) => {
    setAdditionalInfo((prev) => ({
      ...prev,
      state: (value || "").toString().toUpperCase(),
    }));
  };

  // Deletar anexo de uma tarefa em edição
  const handleDeleteAttachment = async (t, att) => {
    const cardId = t.cardId || t.card_id || t.CardId || task?.id;
    const attId = att.id || att.objectName || att.gcsKey;
    if (!attId) return;

    const ok = window.confirm(
      `Remover o anexo "${
        att.originalName || att.filename || att.name || "arquivo"
      }"?`
    );
    if (!ok) return;

    try {
      await axios.delete(CRM.TASK_ATTACHMENT_URL(cardId, t.id, attId));
      setTasks((prev) =>
        (prev || []).map((it) =>
          it.id === t.id
            ? {
                ...it,
                attachments: (it.attachments || []).filter(
                  (a) => (a.id || a.objectName || a.gcsKey) !== attId
                ),
              }
            : it
        )
      );
      const updatedAt = new Date().toISOString();
      updateTask({ ...task, updatedAt });
      setSelectedTask?.((prev) => ({ ...prev, updatedAt }));
    } catch (e) {
      console.error("Erro ao excluir anexo:", e);
      alert("Não foi possível excluir o anexo.");
    }
  };

  // ===== Criar TAREFA =====
  const handleSaveNextAction = async () => {
    const normalizedNote = toRichHtml(actionNoteHtml);
    const cleanHtml = stripVisualEmpty(normalizedNote);
    if (
      !cleanHtml &&
      !actionType &&
      !actionDueDate &&
      !actionDueTime &&
      actionFiles.length === 0
    )
      return;

    try {
      const dueDateISO = buildLocalISO(actionDueDate, actionDueTime);
      const responsibleIds = (actionResponsibleIds || []).filter(Boolean);
      const primaryResponsibleId = responsibleIds[0] || null;

      const basePayload = {
        type: actionType || null,
        note: cleanHtml || "",
        dueDate: dueDateISO || null,
        responsibleId: primaryResponsibleId,
        responsibleIds: responsibleIds.length ? responsibleIds : undefined,
      };

      let response;

      if (actionFiles && actionFiles.length > 0) {
        setIsUploadingTask(true);
        setUploadPct(0);

        const fd = new FormData();
        Object.entries(basePayload).forEach(([k, v]) => {
          if (v !== undefined && v !== null) fd.append(k, v);
        });
        actionFiles.forEach((f) => fd.append("attachments", f));

        response = await axios.post(
          `${CRM.ADD_CARD_HISTORY}/${task?.id}/tasks`,
          fd,
          {
            headers: { "Content-Type": undefined },
            onUploadProgress: (e) => {
              if (e && e.total) {
                const pct = Math.round((e.loaded * 100) / e.total);
                setUploadPct(pct);
              }
            },
          }
        );
      } else {
        response = await axios.post(
          `${CRM.ADD_CARD_HISTORY}/${task?.id}/tasks`,
          basePayload,
          { headers: { "Content-Type": undefined } }
        );
      }

      const created =
        response?.data?.data ?? response?.data?.task ?? response?.data ?? null;

      const responseHasAttachments =
        Array.isArray(created?.attachments) && created.attachments.length > 0;
      const shouldReloadTasks =
        !created?.id ||
        ((actionFiles || []).length > 0 && !responseHasAttachments);

      if (!shouldReloadTasks && created?.id) {
        setTasks((prev) => {
          const next = [created, ...(prev || [])];
          next.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
          return next;
        });
      } else {
        await reloadTasks();
      }

      const updatedAt = new Date().toISOString();
      updateTask({ ...task, updatedAt });
      setSelectedTask?.((prev) => ({ ...prev, updatedAt }));
      await onRefetchCard?.(task?.id);

      setActionType("");
      setActionNoteHtml("");
      setActionDueDate("");
      setActionDueTime("");
      setActionResponsibleIds(extractResponsibleIds(task));
      setActionFiles([]);
      setIsEditingTask(false);
      invalidateStats(task?.id);
    } catch (err) {
      console.error("Erro ao salvar ação:", err);
      window.alert("Não foi possível salvar a tarefa.");
    } finally {
      setIsUploadingTask(false);
      setUploadPct(0);
    }
  };

  // ===== Editar / concluir / excluir TAREFA =====
  const startEditTask = (t) => {
    setEditingTaskId(t.id);
    setEditType(t.type || "");
    setEditNoteHtml(t.note || "");
    setEditDueDate(t?.dueDate ? String(t.dueDate).slice(0, 10) : "");
    setEditDueTime(t?.dueDate ? dateToHHMM(t.dueDate) : "");
    setEditResponsibleIds(extractResponsibleIds(t));
    setTaskBeingEdited(t);
    setEditModalOpen(true);
  };

  const cancelEditTask = () => {
    setEditingTaskId(null);
    setEditType("");
    setEditNoteHtml("");
    setEditDueDate("");
    setEditDueTime("");
    setEditResponsibleIds([]);
    invalidateStats(task?.id);
  };

  const saveEditTask = async (t) => {
    try {
      const dueISO = buildLocalISO(editDueDate, editDueTime);
      const htmlNow = toRichHtml(editNoteHtml || "");
      const cleanHtml = stripVisualEmpty(htmlNow);
      const responsibleIds = (editResponsibleIds || []).filter(Boolean);
      const primaryResponsibleId = responsibleIds[0] || null;

      const basePayload = {
        type: editType || null,
        note: cleanHtml,
        dueDate: dueISO || null,
        responsibleId: primaryResponsibleId,
        responsibleIds: responsibleIds.length ? responsibleIds : undefined,
      };

      if (editFiles && editFiles.length > 0) {
        const fd = new FormData();
        Object.entries(basePayload).forEach(([k, v]) => {
          if (v !== undefined && v !== null) fd.append(k, v);
        });
        editFiles.forEach((f) => fd.append("attachments[]", f));
        await axios.put(`${CRM.UPDATE_TASK}/${t.id}`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      } else {
        await axios.put(`${CRM.UPDATE_TASK}/${t.id}`, basePayload);
      }

      setTasks((prev) =>
        (prev || []).map((it) =>
          it.id === t.id
            ? {
                ...it,
                ...basePayload,
                responsibles: (responsibleIds || []).map((rid) => {
                  const user = availableUsers.find(
                    (u) => String(u.id) === String(rid)
                  );
                  return user ? { id: user.id, name: user.name } : { id: rid };
                }),
              }
            : it
        )
      );

      const updatedAt = new Date().toISOString();
      updateTask({ ...task, updatedAt });
      setSelectedTask?.((prev) => ({ ...prev, updatedAt }));

      await onRefetchCard?.(task?.id);
      setEditFiles([]);
      cancelEditTask();
    } catch (err) {
      console.error("Erro ao editar tarefa:", err);
      window.alert("Não foi possível salvar as alterações.");
    }
  };

  const registerTaskHistory = async (type, t) => {
    try {
      await axios.post(`${CRM.ADD_CARD_HISTORY}/${task?.id}/history`, {
        type,
        taskId: t.id,
        text:
          type === "task_completed"
            ? "Tarefa marcada como concluída."
            : "Tarefa reaberta.",
      });
    } catch (e) {}
  };

  const handleToggleTaskStatus = async (t, shouldComplete) => {
    try {
      if (shouldComplete) {
        const res = await axios.post(`${CRM.UPDATE_TASK}/${t.id}/complete`);
        const completedTask = res?.data?.data;
        if (completedTask) {
          setTasks((prev) =>
            (prev || []).map((it) => (it.id === t.id ? completedTask : it))
          );
        } else {
          setTasks((prev) =>
            (prev || []).map((it) =>
              it.id === t.id
                ? {
                    ...it,
                    status: "done",
                    completedAt: new Date().toISOString(),
                  }
                : it
            )
          );
        }
        await registerTaskHistory("task_completed", t);
      } else {
        const res = await axios.put(`${CRM.UPDATE_TASK}/${t.id}`, {
          status: "open",
          completedAt: null,
        });
        const reopenedTask = res?.data?.data;
        if (reopenedTask) {
          setTasks((prev) =>
            (prev || []).map((it) => (it.id === t.id ? reopenedTask : it))
          );
        } else {
          setTasks((prev) =>
            (prev || []).map((it) =>
              it.id === t.id ? { ...it, status: "open", completedAt: null } : it
            )
          );
        }
        await registerTaskHistory("task_reopened", t);
      }
      const updatedAt = new Date().toISOString();
      updateTask({ ...task, updatedAt });
      setSelectedTask?.((prev) => ({ ...prev, updatedAt }));
      await onRefetchCard?.(task?.id);
      invalidateStats(task?.id);
    } catch (err) {
      console.error("Erro ao atualizar status:", err);
      window.alert("Não foi possível atualizar o status da tarefa.");
    }
  };

  const handleDeleteTask = async (t) => {
    try {
      const ok = window.confirm("Deseja excluir esta tarefa?");
      if (!ok) return;
      await axios.delete(`${CRM.DELETE_TASK}/${t.id}`);
      setTasks((prev) => (prev || []).filter((it) => it.id !== t.id));
      const updatedAt = new Date().toISOString();
      updateTask({ ...task, updatedAt });
      setSelectedTask?.((prev) => ({ ...prev, updatedAt }));
      await onRefetchCard?.(task?.id);
      invalidateStats(task?.id);
    } catch (err) {
      console.error("Erro ao excluir tarefa:", err);
      window.alert("Não foi possível excluir a tarefa.");
    }
  };

  // ===== Comentários =====
  const handleAddComment = async () => {
    const normalized = toRichHtml(commentHtml);
    const clean = stripVisualEmpty(normalized);
    if (!clean) return;
    try {
      const response = await axios.post(
        `${CRM.ADD_CARD_HISTORY}/${task?.id}/history`,
        { text: clean, type: "comment" }
      );
      const created = response?.data?.data;
      if (created && (created.type || "comment") === "comment") {
        const enriched = {
          ...created,
          userId: created.userId || created.user_id || currentUser?.id || null,
          User:
            created.User ||
            (currentUser
              ? { id: currentUser.id, name: currentUser.name }
              : undefined),
        };
        setHistory((prev) => [enriched, ...(prev || [])]);
      }
      const updatedAt = new Date().toISOString();
      updateTask({ ...task, updatedAt });
      setSelectedTask?.((prev) => ({ ...prev, updatedAt }));
      setCommentHtml("");
    } catch (err) {
      console.error("Erro ao adicionar comentário:", err);
    }
  };

  // ===== Editar/Excluir Comentário =====
  const apiUpdateComment = (commentId, payload) => {
    const tries = [
      () =>
        axios.put(
          `${CRM.ADD_CARD_HISTORY}/${task?.id}/history/${commentId}`,
          payload
        ),
      () =>
        axios.patch(
          `${CRM.ADD_CARD_HISTORY}/${task?.id}/history/${commentId}`,
          payload
        ),
    ];
    return tries.reduce(
      (p, fn) => p.catch(fn),
      Promise.reject(new Error("try next"))
    );
  };

  const apiDeleteComment = (commentId) => {
    const tries = [
      () =>
        axios.delete(
          `${CRM.ADD_CARD_HISTORY}/${task?.id}/history/${commentId}`
        ),
    ];
    return tries.reduce(
      (p, fn) => p.catch(fn),
      Promise.reject(new Error("try next"))
    );
  };

  const handleSaveEditedComment = async (h) => {
    const normalized = toRichHtml(editCommentHtml);
    const clean = stripVisualEmpty(normalized);
    if (!clean) {
      if (
        !window.confirm("O texto ficou vazio. Deseja excluir este comentário?")
      )
        return;
      return handleDeleteComment(h);
    }
    try {
      await apiUpdateComment(h.id, {
        text: clean,
        type: "comment",
        editedAt: new Date().toISOString(),
        isEdited: true,
      });

      setHistory((prev) =>
        (prev || []).map((it) =>
          it.id === h.id
            ? {
                ...it,
                text: clean,
                editedAt: new Date().toISOString(),
                isEdited: true,
              }
            : it
        )
      );
      setEditingCommentId(null);
      setEditCommentHtml("");

      const updatedAt = new Date().toISOString();
      updateTask({ ...task, updatedAt });
      setSelectedTask?.((prev) => ({ ...prev, updatedAt }));
    } catch (e) {
      console.error("Erro ao editar comentário:", e);
      alert("Não foi possível salvar a edição do comentário.");
    }
  };

  const handleDeleteComment = async (h) => {
    const ok = window.confirm("Excluir este comentário?");
    if (!ok) return;
    try {
      await apiDeleteComment(h.id);
      setHistory((prev) => (prev || []).filter((it) => it.id !== h.id));

      const updatedAt = new Date().toISOString();
      updateTask({ ...task, updatedAt });
      setSelectedTask?.((prev) => ({ ...prev, updatedAt }));
    } catch (e) {
      console.error("Erro ao excluir comentário:", e);
      alert("Não foi possível excluir o comentário.");
    }
  };

  const handleGoToStage = async (targetStageId) => {
    if (!targetStageId || targetStageId === task.stageId) return;
    try {
      await onMoveStage?.(task.id, targetStageId);
      const updatedAt = new Date().toISOString();
      setSelectedTask?.((prev) => ({
        ...prev,
        stageId: targetStageId,
        updatedAt,
      }));
    } catch (e) {
      console.error("Erro ao mover stage via timeline:", e);
    }
  };

  const lastTask =
    Array.isArray(tasks) && tasks.length
      ? [...tasks].sort(
          (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
        )[0]
      : null;

  const commentOnlyHistory = Array.isArray(history)
    ? history.filter((h) => (h?.type || "comment") === "comment")
    : [];

  const isCommentOwner = (h) => {
    const uid = h.userId || h.user_id || h.User?.id;
    return currentUser?.id && String(uid) === String(currentUser.id);
  };

  // ===== Valor da venda (visual + edit) + FEE =====
  const [isEditingDeal, setIsEditingDeal] = useState(false);
  const [dealValue, setDealValue] = useState(
    Number(task?.dealValue ?? task?.value ?? task?.amount) || 0
  );
  const [dealInput, setDealInput] = useState(
    (
      Number(task?.dealValue ?? task?.value ?? task?.amount) || 0
    ).toLocaleString("pt-BR", { style: "currency", currency: "BRL" })
  );

  const [feeValue, setFeeValue] = useState(
    Number(task?.feeValue ?? task?.fee ?? 0) || 0
  );
  const [feeInput, setFeeInput] = useState(
    (Number(task?.feeValue ?? task?.fee ?? 0) || 0).toLocaleString("pt-BR", {
      style: "currency",
      currency: "BRL",
    })
  );

  // ===== Bancos / instituição da venda =====
  const [banks, setBanks] = useState([]);
  const [loadingBanks, setLoadingBanks] = useState(false);
  const [selectedInstitutionIds, setSelectedInstitutionIds] = useState([]);

  // Re-sincroniza instituições quando trocar de card
  useEffect(() => {
    // 1) Se o back mandar dealBankIds pronto (ex: [1, 3, 7])
    if (Array.isArray(task?.dealBankIds) && task.dealBankIds.length) {
      setSelectedInstitutionIds(task.dealBankIds.map((id) => String(id)));
      return;
    }

    // 2) Fallback: se vier só o include de dealBanks (objetos)
    if (Array.isArray(task?.dealBanks) && task.dealBanks.length) {
      setSelectedInstitutionIds(task.dealBanks.map((bank) => String(bank.id)));
      return;
    }

    // 3) Nenhum banco
    setSelectedInstitutionIds([]);
  }, [task?.id, task?.dealBankIds, task?.dealBanks]);

  // Buscar bancos (todas instituições com as quais temos relacionamento)
  useEffect(() => {
    const fetchBanks = async () => {
      try {
        setLoadingBanks(true);
        // ajuste a rota conforme seu apiConfig (ex: CRM.BANKS_APROVADOS ou CRM.BANKS)
        const res = await axios.get(CRM.BANKS);
        const data =
          res?.data?.data?.data || res?.data?.data || res?.data || [];
        setBanks(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error("Erro ao buscar bancos/instituições:", err);
      } finally {
        setLoadingBanks(false);
      }
    };

    fetchBanks();
  }, []);

  const [approvalRows, setApprovalRows] = useState(
    Array.isArray(task?.dealApprovals) ? task.dealApprovals : []
  );
  const [loadingApprovals, setLoadingApprovals] = useState(false);
  const [approvalBankId, setApprovalBankId] = useState("");
  const [approvalAmountInput, setApprovalAmountInput] = useState("");
  const [approvalDecisionDate, setApprovalDecisionDate] = useState(
    new Date().toISOString().slice(0, 10)
  );
  const [approvalApproved, setApprovalApproved] = useState(true);
  const [approvalNotes, setApprovalNotes] = useState("");
  const [savingApproval, setSavingApproval] = useState(false);
  const [editingApprovalId, setEditingApprovalId] = useState(null);

  const loadApprovals = useCallback(async () => {
    if (!task?.id) {
      setApprovalRows([]);
      return;
    }
    setLoadingApprovals(true);
    try {
      const res = await axios.get(`${CRM.CARD_APPROVALS}/${task.id}/approvals`);
      const data = Array.isArray(res?.data?.data) ? res.data.data : [];
      setApprovalRows(data);
    } catch (err) {
      console.error("Erro ao carregar valores aprovados:", err);
    } finally {
      setLoadingApprovals(false);
    }
  }, [task?.id]);

  useEffect(() => {
    setApprovalRows(
      Array.isArray(task?.dealApprovals) ? task.dealApprovals : []
    );
  }, [task?.id, task?.dealApprovals]);

  useEffect(() => {
    loadApprovals();
  }, [loadApprovals]);

  useEffect(() => {
    const v = Number(task?.dealValue ?? task?.value ?? task?.amount) || 0;
    setDealValue(v);
    setDealInput(
      v.toLocaleString("pt-BR", { style: "currency", currency: "BRL" })
    );

    const f = Number(task?.feeValue ?? task?.fee ?? 0) || 0;
    setFeeValue(f);
    setFeeInput(
      f.toLocaleString("pt-BR", { style: "currency", currency: "BRL" })
    );

    setActionResponsibleIds(extractResponsibleIds(task));
  }, [task?.id]);

  const onEditDeal = () => setIsEditingDeal(true);
  const onCancelDeal = () => {
    setIsEditingDeal(false);
    setDealInput(
      dealValue.toLocaleString("pt-BR", { style: "currency", currency: "BRL" })
    );
    setFeeInput(
      feeValue.toLocaleString("pt-BR", { style: "currency", currency: "BRL" })
    );
  };
  const onlyDigits = (s) => (s || "").replace(/\D/g, "");
  const onChangeDeal = (e) => {
    const digits = onlyDigits(e.target.value);
    const money = Number(digits || "0") / 100 || 0;
    setDealInput(
      money.toLocaleString("pt-BR", { style: "currency", currency: "BRL" })
    );
  };
  const onChangeFee = (e) => {
    const digits = onlyDigits(e.target.value);
    const money = Number(digits || "0") / 100 || 0;
    setFeeInput(
      money.toLocaleString("pt-BR", { style: "currency", currency: "BRL" })
    );
  };
  const toNumberFromMasked = (s) => {
    const cents = Number(onlyDigits(s));
    if (!Number.isFinite(cents)) return 0;
    return cents / 100;
  };

  const onChangeApprovalAmount = (e) => {
    const digits = onlyDigits(e.target.value);
    const money = Number(digits || "0") / 100 || 0;
    setApprovalAmountInput(
      money.toLocaleString("pt-BR", { style: "currency", currency: "BRL" })
    );
  };

  const handleApprovalBankChange = (e) => setApprovalBankId(e.target.value);
  const handleApprovalDecisionDateChange = (e) =>
    setApprovalDecisionDate(e.target.value);
  const handleApprovalStatusChange = (e) =>
    setApprovalApproved(e.target.value === "approved");
  const handleApprovalNotesChange = (e) => setApprovalNotes(e.target.value);
  const handleRejectionReasonSelect = (value) => {
    if (!value) return;
    setApprovalNotes(value);
  };

  const resetApprovalForm = useCallback(() => {
    setApprovalBankId("");
    setApprovalAmountInput("");
    setApprovalDecisionDate(new Date().toISOString().slice(0, 10));
    setApprovalApproved(true);
    setApprovalNotes("");
    setEditingApprovalId(null);
  }, []);

  const handleEditApproval = (approval) => {
    if (!approval) return;
    const bankId =
      approval.bankId || approval.bank?.id || approval.bank_id || "";
    const rawDate = approval.decisionAt || approval.decision_at || null;
    const parsedDate = rawDate ? new Date(rawDate) : new Date();
    const safeDate = Number.isNaN(parsedDate.getTime())
      ? new Date()
      : parsedDate;

    setEditingApprovalId(approval.id);
    setApprovalBankId(bankId ? String(bankId) : "");
    setApprovalAmountInput(formatCurrencyValue(approval.amount ?? ""));
    setApprovalDecisionDate(safeDate.toISOString().slice(0, 10));
    setApprovalApproved(Boolean(approval.approved));
    setApprovalNotes(
      approval.notes || approval.declineReason || approval.decline_reason || ""
    );
  };

  const handleSaveApproval = async () => {
    if (!task?.id) return;
    if (!approvalBankId) {
      window.alert("Escolha a instituição que liberou o valor.");
      return;
    }
    const numericAmount = toNumberFromMasked(approvalAmountInput);
    if (!numericAmount) {
      window.alert("Informe o valor aprovado.");
      return;
    }
    setSavingApproval(true);
    try {
      const payload = {
        bankId: Number(approvalBankId),
        amount: numericAmount,
        approved: approvalApproved,
        decisionAt: approvalDecisionDate,
        notes: approvalNotes || null,
        declineReason: approvalApproved ? null : approvalNotes || null,
      };
      const baseUrl = `${CRM.CARD_APPROVALS}/${task.id}/approvals`;
      const res = editingApprovalId
        ? await axios.put(`${baseUrl}/${editingApprovalId}`, payload)
        : await axios.post(baseUrl, payload);
      const saved = res?.data?.data;
      await loadApprovals();
      resetApprovalForm();
      if (saved && !editingApprovalId) {
        const updatedTask = {
          ...task,
          dealApprovals: [saved, ...(task?.dealApprovals || [])],
        };
        updateTask?.(updatedTask);
        setSelectedTask?.(updatedTask);
      }
    } catch (error) {
      console.error("Erro ao salvar valor aprovado:", error);
      window.alert(
        error?.response?.data?.message ||
          "Não foi possível salvar o valor aprovado."
      );
    } finally {
      setSavingApproval(false);
    }
  };

  const approvalPanelProps = {
    approvalRows,
    loadingApprovals,
    banks,
    rejectionReasons: DEFAULT_REJECTION_REASONS,
    approvalBankId,
    onChangeApprovalBankId: handleApprovalBankChange,
    approvalAmountInput,
    onChangeApprovalAmount,
    approvalDecisionDate,
    onChangeApprovalDecisionDate: handleApprovalDecisionDateChange,
    approvalApproved,
    onChangeApprovalApproved: handleApprovalStatusChange,
    approvalNotes,
    onChangeApprovalNotes: handleApprovalNotesChange,
    onSelectRejectionReason: handleRejectionReasonSelect,
    editingApprovalId,
    onEditApproval: handleEditApproval,
    onCancelEditApproval: resetApprovalForm,
    handleSaveApproval,
    savingApproval,
  };

  const handleChangeInstitutions = async (ids = []) => {
    const normalized = (ids || []).map((id) => String(id));
    setSelectedInstitutionIds(normalized);

    try {
      await axios.put(`${CRM.UPDATE_CARD}/${task.id}`, {
        dealBankIds: normalized, // <- manda ids
      });

      const selectedBanks = (banks || []).filter((b) =>
        normalized.includes(String(b.id))
      );

      const updatedAt = new Date().toISOString();

      updateTask({
        ...task,
        dealBankIds: normalized,
        dealBanks: selectedBanks,
        updatedAt,
      });

      setSelectedTask?.((prev) => ({
        ...prev,
        dealBankIds: normalized,
        dealBanks: selectedBanks,
        updatedAt,
      }));
    } catch (e) {
      console.error("Erro ao salvar instituições do negócio:", e);
      window.alert("Não foi possível salvar as instituições da venda.");
    }
  };

  const onSaveDeal = async () => {
    const numericDeal = toNumberFromMasked(dealInput);
    const numericFee = toNumberFromMasked(feeInput);
    try {
      await axios.put(`${CRM.UPDATE_CARD}/${task?.id}`, {
        dealValue: numericDeal,
        feeValue: numericFee,
      });
      setDealValue(numericDeal);
      setFeeValue(numericFee);
      const updatedAt = new Date().toISOString();
      updateTask({
        ...task,
        dealValue: numericDeal,
        feeValue: numericFee,
        updatedAt,
      });
      setSelectedTask?.((prev) => ({
        ...prev,
        dealValue: numericDeal,
        feeValue: numericFee,
        updatedAt,
      }));
      setIsEditingDeal(false);
    } catch (e) {
      console.error("Erro ao salvar valor da venda/fee:", e);
      window.alert("Não foi possível salvar o valor da venda/fee.");
    }
  };

  // menu de ações: controle do aberto
  const [openMenuId, setOpenMenuId] = useState(null);
  const menuBtnRef = useRef(null);

  // fecha ao clicar fora / ESC
  useEffect(() => {
    const onDocClick = (e) => {
      if (!e.target.closest?.('[data-task-menu-root="true"]')) {
        setOpenMenuId(null);
      }
    };
    const onEsc = (e) => {
      if (e.key === "Escape") setOpenMenuId(null);
    };
    document.addEventListener("click", onDocClick);
    document.addEventListener("keydown", onEsc);
    return () => {
      document.removeEventListener("click", onDocClick);
      document.removeEventListener("keydown", onEsc);
    };
  }, []);

  // fecha ao scroll/resize
  useEffect(() => {
    const close = () => setOpenMenuId((prev) => (prev ? null : prev));
    window.addEventListener("scroll", close, true);
    window.addEventListener("resize", close);
    return () => {
      window.removeEventListener("scroll", close, true);
      window.removeEventListener("resize", close);
    };
  }, []);

  function DropdownPortal({
    open,
    anchorRect,
    width = 176,
    offset = 6,
    children,
  }) {
    if (!open || !anchorRect) return null;

    const viewportW = window.innerWidth || document.documentElement.clientWidth;
    const viewportH =
      window.innerHeight || document.documentElement.clientHeight;

    const estimatedLeft = Math.min(
      Math.max(8, anchorRect.right - width),
      Math.max(8, viewportW - width - 8)
    );

    const estimatedTop = Math.min(
      Math.max(8, anchorRect.bottom + offset),
      Math.max(8, viewportH - 8)
    );

    return ReactDOM.createPortal(
      <div
        role="menu"
        style={{
          position: "fixed",
          top: estimatedTop,
          left: estimatedLeft,
          width,
          zIndex: 9999,
        }}
        data-task-menu-root="true"
        onClick={(e) => e.stopPropagation()}
        className="bg-white border rounded-md shadow-lg overflow-hidden"
      >
        {children}
      </div>,
      document.body
    );
  }

  const renderAdditionalInfoInputs = () => (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
      <div>
        <label className="text-xs text-gray-600 block mb-1">Cidade</label>
        <TextInput
          value={additionalInfo.city}
          onChange={(e) => updateAdditionalInfoField("city", e.target.value)}
          placeholder="Cidade ou estado do cliente"
        />
      </div>
      <div>
        <label className="text-xs text-gray-600 block mb-1">
          Endividamento
        </label>
        <TextInput
          value={additionalInfo.indebtedness}
          onChange={(e) =>
            updateAdditionalInfoField("indebtedness", e.target.value)
          }
          placeholder="Breve descrição do endividamento"
        />
      </div>
      <div className="md:col-span-2">
        <label className="text-xs text-gray-600 block mb-1">
          Necessidade de crédito
        </label>
        <TextInput
          value={additionalInfo.creditNeed}
          onChange={(e) =>
            updateAdditionalInfoField("creditNeed", e.target.value)
          }
          placeholder="Objetivo do crédito"
        />
      </div>
      <div className="sm:col-span-2">
        <label className="flex items-center gap-2 text-sm font-semibold text-gray-700">
          <input
            type="checkbox"
            checked={additionalInfo.hasGuarantee}
            onChange={(e) =>
              updateAdditionalInfoField("hasGuarantee", e.target.checked)
            }
            className="h-4 w-4 rounded border-gray-300 text-gray-900 focus:ring-2 focus:ring-blue-500"
          />
          <span>Possui garantia?</span>
        </label>
      </div>
      {additionalInfo.hasGuarantee && (
        <div className="sm:col-span-2">
          <label className="text-xs text-gray-600 block mb-1">
            Detalhes da garantia
          </label>
          <Textarea
            value={additionalInfo.guaranteeDetails}
            onChange={(e) =>
              updateAdditionalInfoField("guaranteeDetails", e.target.value)
            }
            rows={3}
            placeholder="Descrição da garantia, valor e vigência"
          />
        </div>
      )}
      <div className="sm:col-span-2">
        <label className="text-xs text-gray-600 block mb-1">
          Apresentação institucional / breve histórico
        </label>
        <Textarea
          value={additionalInfo.institutionalPresentation}
          onChange={(e) =>
            updateAdditionalInfoField(
              "institutionalPresentation",
              e.target.value
            )
          }
          rows={3}
          placeholder="Resumo institucional ou histórico recente"
        />
      </div>
      <div className="sm:col-span-2">
        <label className="text-xs text-gray-600 block mb-1">Outros</label>
        <Textarea
          value={additionalInfo.others}
          onChange={(e) => updateAdditionalInfoField("others", e.target.value)}
          rows={3}
          placeholder="Informações adicionais livres"
        />
      </div>
    </div>
  );

  return (
    <Modal show={true} onClose={handleClose} size="8xl" className="!p-0">
      {/* ===== MOBILE ===== */}
      <div className="lg:hidden fixed inset-0 bg-white flex flex-col">
        {/* HEADER MOBILE */}
        <div className="sticky top-0 z-20 border-b bg-white px-3 py-2 flex items-center justify-between gap-2">
          <h2 className="text-base font-semibold truncate">
            {task?.lead?.name || "Cliente"}
          </h2>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={handleDeleteCard}
              disabled={deleting}
              title="Excluir card"
              className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md border text-xs ${
                deleting
                  ? "bg-gray-100 border-gray-200 text-gray-400 cursor-not-allowed"
                  : "bg-red-50 border-red-200 text-red-700 hover:bg-red-100"
              }`}
            >
              <HiOutlineTrash size={14} />
              {deleting ? "Excl…" : "Excluir"}
            </button>
            <Button size="xs" color="light" onClick={handleClose}>
              Fechar
            </Button>
          </div>
        </div>

        {/* ETAPA MOBILE */}
        <div className="px-3 py-2 bg-gray-50 border-b">
          <label className="text-xs text-gray-600 block">Etapa</label>
          <Select
            value={task?.stageId}
            onChange={(e) => handleGoToStage(e.target.value)}
          >
            {(stages || []).map((st) => (
              <option key={st.id} value={st.id}>
                {st.name}
              </option>
            ))}
          </Select>
        </div>

        {/* TABS MOBILE */}
        <div className="sticky top-[94px] z-10 bg-white border-b">
          <div className="flex overflow-x-auto no-scrollbar">
            {[
              { k: "detalhes", label: "Detalhes" },
              { k: "tarefas", label: `Tarefas (${tasks?.length || 0})` },
              {
                k: "comentarios",
                label: `Comentários (${commentOnlyHistory.length})`,
              },
              { k: "informacoes", label: "Informações" },
              { k: "acao", label: "Nova ação" },
            ].map((tab) => (
              <button
                key={tab.k}
                onClick={() => setMobileTab(tab.k)}
                className={`px-3 py-2 text-sm border-b-2 -mb-px whitespace-nowrap ${
                  mobileTab === tab.k
                    ? "border-gray-900 text-gray-900"
                    : "border-transparent text-gray-500"
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        {/* BODY MOBILE */}
        <div className="flex-1 overflow-y-auto p-3 space-y-3">
          {mobileTab === "detalhes" && (
            <>
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
                <div className="flex items-center justify-between mb-2">
                  <h4 className="text-sm font-semibold text-gray-800">
                    Detalhes do cliente
                  </h4>
                  {!isEditingClient && (
                    <Button size="xs" color="light" onClick={openClientEditor}>
                      <HiOutlinePencil className="mr-1" /> Editar
                    </Button>
                  )}
                </div>

                {/* Visualização / Edição */}
                {!isEditingClient ? (
                  <>
                    <div className="grid grid-cols-1 gap-2 text-sm">
                      <div className="flex flex-col gap-1">
                        <div className="font-semibold">Responsável</div>
                        <span className="text-gray-700">
                          {customerResponsible || "—"}
                        </span>
                      </div>
                      <div className="flex flex-col gap-1">
                        <div className="font-semibold">Documento</div>
                        <span className="text-gray-700">
                          {customerDocument
                            ? formatDocument(customerDocument)
                            : "—"}
                        </span>
                      </div>
                      <div className="flex flex-col gap-1">
                        <div className="font-semibold">E-mail</div>
                        <span className="text-gray-700">
                          {customerEmail || "—"}
                        </span>
                      </div>
                      <div className="flex flex-col gap-1">
                        <div className="font-semibold">Telefone</div>
                        <span className="text-gray-700">
                          {customerPhone || "—"}
                        </span>
                      </div>
                      <div className="flex flex-col gap-1">
                        <div className="font-semibold">
                          Faturamento presumido
                        </div>
                        <span className="text-gray-700">
                          {additionalInfo.presumedRevenue || "—"}
                        </span>
                      </div>
                      <div className="flex flex-col gap-1">
                        <div className="font-semibold">Estado</div>
                        <span className="text-gray-700">
                          {additionalInfo.state || "—"}
                        </span>
                      </div>
                      <div className="flex flex-col gap-1">
                        <div className="font-semibold">Origem do lead</div>
                        <span className="text-gray-700">
                          {formatLeadSourceLabel(resolveLeadSource(task?.lead))}
                        </span>
                      </div>
                    </div>

                    <div className="mt-3 flex flex-wrap gap-2">
                      <Button
                        size="xs"
                        color="light"
                        disabled={!customerEmail}
                        onClick={() =>
                          customerEmail &&
                          window.open(`mailto:${customerEmail}`, "_blank")
                        }
                      >
                        <HiMail className="mr-1" /> E-mail
                      </Button>
                      <Button
                        size="xs"
                        color="light"
                        disabled={!customerPhone}
                        onClick={() =>
                          customerPhone &&
                          window.open(
                            `tel:${customerPhone.replace(/\D/g, "")}`,
                            "_blank"
                          )
                        }
                      >
                        <HiPhone className="mr-1" /> Ligar
                      </Button>
                      <Button
                        size="xs"
                        color="light"
                        disabled={!customerPhone}
                        onClick={() => {
                          if (!customerPhone) return;
                          const phone = customerPhone.replace(/\D/g, "");
                          window.open(`https://wa.me/${phone}`, "_blank");
                        }}
                      >
                        <HiChatAlt2 className="mr-1" /> WhatsApp
                      </Button>
                    </div>

                    {showTagPicker && companyIdForTags && (
                      <TagPicker
                        companyId={companyIdForTags}
                        cardId={task?.id}
                        currentTag={currentTag}
                        currentTags={currentTags}
                        onAssigned={handleAssignTags}
                        onClose={() => setShowTagPicker(false)}
                      />
                    )}
                    {showTagPicker && !companyIdForTags && (
                      <div className="text-xs text-red-600 mt-2">
                        Não foi possível identificar a empresa do card para
                        listar etiquetas.
                      </div>
                    )}
                  </>
                ) : (
                  // Edição
                  <div className="flex flex-col gap-2">
                    <div>
                      <label className="text-xs text-gray-600 block mb-1">
                        Nome
                      </label>
                      <TextInput
                        value={clientName}
                        onChange={(e) => setClientName(e.target.value)}
                      />
                    </div>
                    <div>
                      <label className="text-xs text-gray-600 block mb-1">
                        E-mail
                      </label>
                      <TextInput
                        type="email"
                        value={clientEmail}
                        onChange={(e) => setClientEmail(e.target.value)}
                      />
                    </div>
                    <div>
                      <label className="text-xs text-gray-600 block mb-1">
                        Telefone
                      </label>
                      <TextInput
                        value={clientPhone}
                        onChange={(e) => setClientPhone(e.target.value)}
                        placeholder="(11) 99999-9999"
                      />
                    </div>
                    <div>
                      <label className="text-xs text-gray-600 block mb-1">
                        Documento
                      </label>
                      <TextInput
                        value={clientDocument}
                        onChange={(e) => setClientDocument(e.target.value)}
                      />
                    </div>
                    <div>
                      <label className="text-xs text-gray-600 block mb-1">
                        Responsável
                      </label>
                      <TextInput
                        value={clientResponsible}
                        onChange={(e) => setClientResponsible(e.target.value)}
                      />
                    </div>
                    <div>
                      <label className="text-xs text-gray-600 block mb-1">
                        Faturamento presumido
                      </label>
                      <TextInput
                        value={additionalInfo.presumedRevenue}
                        onChange={(event) =>
                          handlePresumedRevenueInputChange(event.target.value)
                        }
                        onBlur={handlePresumedRevenueBlur}
                        placeholder="R$ 0,00"
                      />
                    </div>
                    <div>
                      <label className="text-xs text-gray-600 block mb-1">
                        Estado
                      </label>
                      <Select
                        value={additionalInfo.state || ""}
                        onChange={(event) =>
                          handleStateChange(event.target.value)
                        }
                        className="bg-white text-gray-900"
                      >
                        <option value="">Selecione um estado</option>
                        {BRAZILIAN_STATES.map((stateOption) => (
                          <option
                            key={stateOption.value}
                            value={stateOption.value}
                          >
                            {stateOption.label}
                          </option>
                        ))}
                      </Select>
                    </div>

                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        onClick={handleSaveClientDetails}
                        disabled={savingClient}
                      >
                        {savingClient ? "Salvando…" : "Salvar"}
                      </Button>
                      <Button
                        size="sm"
                        color="light"
                        onClick={() => {
                          setIsEditingClient(false);
                          const c = task?.lead || {};
                          const cEmail = c?.email || c?.emails?.[0] || "";
                          setClientName(c?.name || "");
                          setClientEmail(cEmail || "");
                          setClientPhone(c?.phoneNumber || "");
                          setClientDocument(c?.document || "");
                          setClientResponsible(
                            c?.responsibleName || c?.name || ""
                          );
                        }}
                      >
                        Cancelar
                      </Button>
                    </div>
                  </div>
                )}
              </div>

              {/* Valor da venda (compacto) + Fee */}
              {/* <div className="rounded-lg border border-gray-600 bg-gradient-to-br from-gray-400 via-gray-500 to-gray-600 p-3 text-white">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="grid place-items-center w-8 h-8 rounded-lg bg-white/15 border border-white/20">
                      <HiCurrencyDollar size={18} />
                    </div>
                    <span className="text-xs uppercase tracking-wide opacity-90">
                      Valor da venda
                    </span>
                  </div>
                  {!isEditingDeal && (
                    <button
                      type="button"
                      onClick={onEditDeal}
                      className="inline-flex items-center gap-2 px-2.5 py-1.5 rounded-md bg-white/10 hover:bg-white/20 border border-white/20 text-white text-xs"
                      title="Editar valores"
                    >
                      <HiOutlinePencil size={16} /> Editar
                    </button>
                  )}
                </div>
                {!isEditingDeal ? (
                  <>
                    <div className="mt-2">
                      <div className="text-2xl font-semibold">
                        {dealValue.toLocaleString("pt-BR", {
                          style: "currency",
                          currency: "BRL",
                        })}
                      </div>
                      <p className="mt-1 text-xs opacity-85">
                        Valor previsto/fechado desta oportunidade.
                      </p>
                    </div>

                    <div className="mt-3 text-xs opacity-90 flex items-baseline gap-1 bg-green-200 w-fit px-2 py-1 rounded-xl text-green-800">
                      <span className="uppercase tracking-wide">
                        Fee / taxa
                      </span>
                      <span className="font-semibold text-green-200">
                        {feeValue.toLocaleString("pt-BR", {
                          style: "currency",
                          currency: "BRL",
                        })}
                      </span>
                    </div>
                  </>
                ) : (
                  <div className="mt-3 space-y-3">
                    <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
                      <TextInput
                        value={dealInput}
                        onChange={onChangeDeal}
                        placeholder="R$ 0,00"
                        aria-label="Valor da venda"
                        className="bg-white text-gray-900 rounded-md"
                        label="Valor da venda"
                      />
                    </div>
                    <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
                      <TextInput
                        value={feeInput}
                        onChange={onChangeFee}
                        placeholder="Fee / taxa (R$)"
                        aria-label="Fee / taxa"
                        className="bg-white text-gray-900 rounded-md"
                      />
                    </div>
                    <div className="flex gap-2">
                      <Button size="sm" color="light" onClick={onSaveDeal}>
                        Salvar
                      </Button>
                      <Button size="sm" color="light" onClick={onCancelDeal}>
                        Cancelar
                      </Button>
                    </div>
                  </div>
                )}
              </div> */}

              <ApprovalValuesPanel {...approvalPanelProps} />

              {/* TAG */}
              <div className="mt-4">
                <div className="flex items-center justify-between mb-2">
                  <h5 className="text-sm font-semibold text-gray-800">
                    Etiquetas do card
                  </h5>
                  <Button
                    size="xs"
                    color="light"
                    onClick={() => setShowTagPicker(true)}
                  >
                    Gerenciar etiquetas
                  </Button>
                </div>

                {currentTags.length ? (
                  <div className="flex flex-wrap items-center gap-2">
                    {currentTags.map((tag) => (
                      <span
                        key={tag.id}
                        className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md text-sm font-medium border"
                        style={{
                          background: `${tag.color || "#6b7280"}20`,
                          color: tag.color || "#6b7280",
                          borderColor: tag.color || "#6b7280",
                        }}
                      >
                        <span
                          className="inline-block h-4 w-6 rounded"
                          style={{ background: tag.color || "#6b7280" }}
                        />
                        {tag.name}
                      </span>
                    ))}
                    <Button
                      size="xs"
                      color="light"
                      onClick={() => handleAssignTags([])}
                      title="Remover etiquetas"
                    >
                      Remover todas
                    </Button>
                  </div>
                ) : (
                  <div className="text-xs text-gray-500">
                    Sem etiqueta atribuída.
                  </div>
                )}
              </div>

              {/* Próxima ação (resumo) */}
            </>
          )}

          {mobileTab === "tarefas" && (
            <TaskListCompact
              tasks={tasks}
              loadingTasks={loadingTasks}
              labelMap={labelMap}
              getUserNameById={getUserNameById}
              startEditTask={startEditTask}
              handleToggleTaskStatus={handleToggleTaskStatus}
              handleDeleteTask={handleDeleteTask}
              menuBtnRef={menuBtnRef}
              openMenuId={openMenuId}
              setOpenMenuId={setOpenMenuId}
              DropdownPortal={DropdownPortal}
              onConfirmComplete={openConfirmComplete}
            />
          )}

          {mobileTab === "comentarios" && (
            <div className="space-y-3">
              {/* Editor simples primeiro */}
              <div className="rounded-xl border border-gray-200 bg-white">
                <div className="px-3 py-2 border-b text-sm font-semibold text-gray-800">
                  Adicionar comentário
                </div>
                <div className="p-3">
                  {!commentRich ? (
                    <>
                      <textarea
                        className="w-full border rounded-md p-2 text-sm"
                        rows={5}
                        value={commentHtml.replace(/<[^>]+>/g, "")}
                        onChange={(e) => setCommentHtml(e.target.value)}
                        placeholder="Escreva seu comentário…"
                      />
                      <div className="mt-2 flex items-center justify-between gap-2">
                        <DictationButton target="comment" />
                        <button
                          className="text-xs text-gray-600 underline"
                          onClick={() => setCommentRich(true)}
                        >
                          Usar formatação avançada
                        </button>
                      </div>
                    </>
                  ) : (
                    <TiptapEditor
                      value={commentHtml}
                      onChange={setCommentHtml}
                      users={(availableUsers || []).map((u) => ({
                        id: u.id,
                        value: u.name || u.email || "Usuário",
                        email: u.email,
                        avatar: u.avatarUrl,
                      }))}
                      placeholder="Escreva seu comentário…"
                    />
                  )}
                  {commentRich && (
                    <div className="mt-2 flex items-center justify-between">
                      <DictationButton target="comment" />
                    </div>
                  )}
                  <div className="mt-2 flex justify-end gap-2">
                    <Button
                      size="sm"
                      color="light"
                      onClick={() =>
                        window.confirm("Limpar texto?") && setCommentHtml("")
                      }
                    >
                      Limpar
                    </Button>
                    <Button size="sm" onClick={handleAddComment}>
                      Enviar
                    </Button>
                  </div>
                </div>
              </div>

              {/* Lista */}
              {commentOnlyHistory.length > 0 ? (
                <div className="space-y-2">
                  {commentOnlyHistory.map((h) => {
                    const name =
                      h.User?.name ||
                      getUserNameById?.(h.userId || h.user_id) ||
                      "Não atribuído";
                    const initials = name
                      .split(" ")
                      .map((s) => s[0])
                      .slice(0, 2)
                      .join("")
                      .toUpperCase();
                    const edited = !!(h.editedAt || h.isEdited);
                    const ts = new Date(h.createdAt).toLocaleString("pt-BR");

                    return (
                      <div
                        key={h.id}
                        className="rounded-xl border border-gray-200 bg-white p-3"
                      >
                        <div className="flex items-center gap-2">
                          <div className="w-8 h-8 rounded-full bg-gray-100 text-gray-700 grid place-items-center text-[11px] font-semibold">
                            {initials}
                          </div>
                          <div className="min-w-0">
                            <div className="text-sm font-medium text-gray-900 truncate">
                              {name}
                            </div>
                            <div className="text-[11px] text-gray-500">
                              {ts}{" "}
                              {edited && (
                                <span className="ml-1 text-gray-400">
                                  (editado)
                                </span>
                              )}
                            </div>
                          </div>
                        </div>

                        {/* Ações do dono do comentário */}
                        {isCommentOwner(h) && (
                          <div className="mt-2 flex gap-3">
                            {editingCommentId !== h.id ? (
                              <>
                                <button
                                  className="text-[12px] text-blue-600 underline"
                                  onClick={() => {
                                    setEditingCommentId(h.id);
                                    setEditCommentHtml(h.text || "");
                                  }}
                                >
                                  Editar
                                </button>
                                <button
                                  className="text-[12px] text-red-600 underline"
                                  onClick={() => handleDeleteComment(h)}
                                >
                                  Excluir
                                </button>
                              </>
                            ) : (
                              <>
                                <button
                                  className="text-[12px] text-gray-700 underline"
                                  onClick={() => {
                                    setEditingCommentId(null);
                                    setEditCommentHtml("");
                                  }}
                                >
                                  Cancelar
                                </button>
                                <button
                                  className="text-[12px] text-blue-600 underline"
                                  onClick={() => handleSaveEditedComment(h)}
                                >
                                  Salvar
                                </button>
                              </>
                            )}
                          </div>
                        )}

                        {/* Conteúdo ou editor inline */}
                        {editingCommentId === h.id ? (
                          <TiptapEditor
                            value={editCommentHtml}
                            onChange={setEditCommentHtml}
                            users={(availableUsers || []).map((u) => ({
                              id: u.id,
                              value: u.name || u.email || "Usuário",
                              email: u.email,
                              avatar: u.avatarUrl,
                            }))}
                            placeholder="Edite seu comentário…"
                          />
                        ) : (
                          <div
                            className="rich-text mt-2 text-gray-800 leading-relaxed text-sm"
                            dangerouslySetInnerHTML={{ __html: h.text || "" }}
                          />
                        )}
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-8 text-sm text-gray-600">
                  Ainda não há comentários.
                </div>
              )}
            </div>
          )}

          {mobileTab === "informacoes" && (
            <div className="space-y-4 rounded-xl border border-gray-200 bg-white p-3">
              <div className="flex items-center justify-between">
                <h4 className="text-sm font-semibold text-gray-800">
                  Informações adicionais
                </h4>
              </div>
              {renderAdditionalInfoInputs()}
              <div className="flex flex-wrap gap-2">
                <Button
                  size="sm"
                  onClick={handleSaveAdditionalInfo}
                  disabled={savingAdditionalInfo}
                >
                  {savingAdditionalInfo ? "Salvando…" : "Salvar"}
                </Button>
              </div>
            </div>
          )}

          {mobileTab === "acao" && (
            <div className="space-y-3">
              {/* Presets */}
              <div className="flex gap-2 overflow-x-auto no-scrollbar">
                {ACTION_PRESETS.map(({ key, label, icon: Icon }) => {
                  const active = actionType === key;
                  return (
                    <button
                      key={key}
                      type="button"
                      onClick={() => {
                        setActionType(key);
                        if (!actionNoteHtml?.trim())
                          setActionNoteHtml(defaultNoteFor(key));
                      }}
                      className={`shrink-0 inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-full border text-xs ${
                        active
                          ? "bg-gray-900 text-white border-gray-900"
                          : "bg-white text-gray-700 border-gray-300"
                      }`}
                    >
                      <Icon size={14} /> {label}
                    </button>
                  );
                })}
              </div>

              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="text-xs text-gray-600 block mb-1">
                    Data do prazo
                  </label>
                  <TextInput
                    type="date"
                    value={actionDueDate}
                    onChange={(e) => setActionDueDate(e.target.value)}
                  />
                </div>
                <div>
                  <label className="text-xs text-gray-600 block mb-1">
                    Horário
                  </label>
                  <input
                    type="time"
                    value={actionDueTime || ""}
                    onChange={(e) => setActionDueTime(e.target.value)}
                    step="60"
                    className="w-full border rounded-md p-2 text-sm"
                  />
                </div>
                {actionType === "whatsapp" && (
                  <div className="col-span-2">
                    <label className="text-xs text-gray-600 block mb-1">
                      Telefone (opcional)
                    </label>
                    <TextInput
                      type="tel"
                      placeholder="DDD + número"
                      value={actionPhone}
                      onChange={(e) => setActionPhone(e.target.value)}
                    />
                  </div>
                )}
                <div className="col-span-2 mt-3">
                  <AutoCompleteInput
                    label="Responsáveis da tarefa"
                    name="task-resp-mobile"
                    options={userOptions}
                    value={actionResponsibleIds}
                    isMulti
                    onChange={(opts) =>
                      setActionResponsibleIds(
                        (opts || []).map((opt) => opt?.value).filter(Boolean)
                      )
                    }
                    isLoading={loadingUsers}
                  />
                </div>
              </div>

              <div>
                <label className="text-xs text-gray-600 block mb-1">
                  Descrição
                </label>
                {!showRichAction ? (
                  <>
                    <textarea
                      className="w-full border rounded-md p-2 text-sm"
                      rows={5}
                      value={actionNoteHtml.replace(/<[^>]+>/g, "")}
                      onChange={(e) => setActionNoteHtml(e.target.value)}
                      placeholder="Detalhe a ação (opcional)"
                    />
                    <div className="mt-2 flex items-center justify-between gap-2">
                      <DictationButton target="action" />
                      <button
                        className="text-xs text-gray-600 underline"
                        onClick={() => setShowRichAction(true)}
                      >
                        Usar formatação avançada
                      </button>
                    </div>
                  </>
                ) : (
                  <TiptapEditor
                    value={actionNoteHtml}
                    onChange={setActionNoteHtml}
                    users={(availableUsers || []).map((u) => ({
                      id: u.id,
                      value: u.name || u.email || "Usuário",
                      email: u.email,
                      avatar: u.avatarUrl,
                      }))}
                    placeholder="Detalhe a ação (opcional)"
                  />
                )}
                {showRichAction && (
                  <div className="mt-2 flex items-center justify-between">
                    <DictationButton target="action" />
                  </div>
                )}
              </div>

              <div className="flex flex-wrap gap-2">
                <Button
                  size="sm"
                  onClick={handleSaveNextAction}
                  disabled={isUploadingTask}
                  aria-busy={isUploadingTask}
                >
                  {isUploadingTask
                    ? uploadPct
                      ? `Enviando… ${uploadPct}%`
                      : "Enviando…"
                    : "Salvar tarefa"}
                </Button>

                {actionType === "whatsapp" && actionPhone && (
                  <Button
                    size="sm"
                    color="success"
                    onClick={() => {
                      const msg = encodeURIComponent(
                        (actionNoteHtml || "")
                          .replace(/<[^>]+>/g, " ")
                          .replace(/\s+/g, " ")
                          .trim()
                      );
                      const phone = actionPhone.replace(/\D/g, "");
                      window.open(
                        `https://wa.me/${phone}?text=${msg}`,
                        "_blank"
                      );
                    }}
                  >
                    Abrir WhatsApp
                  </Button>
                )}
              </div>
            </div>
          )}
        </div>

        {/* FOOTER MOBILE */}
        <div className="sticky bottom-0 z-20 border-t bg-white p-2 grid grid-cols-2 gap-2">
          <Button size="sm" color="light" onClick={handleAddComment}>
            Comentar
          </Button>
          <Button
            size="sm"
            onClick={handleSaveNextAction}
            disabled={isUploadingTask}
            aria-busy={isUploadingTask}
          >
            {isUploadingTask
              ? uploadPct
                ? `Enviando… ${uploadPct}%`
                : "Enviando…"
              : "Salvar tarefa"}
          </Button>
        </div>
      </div>

      {/* ===== DESKTOP ===== */}
      <div className="hidden lg:block overflow-y-auto">
        <Modal.Header className="bg-gray-50 py-2">
          <div className="flex items-center gap-5 justify-between mt-1">
            <h2 className="text-base font-semibold text-gray-800">
              {task?.lead?.name || "Cliente"}
            </h2>
            <button
              type="button"
              onClick={handleDeleteCard}
              disabled={deleting}
              title="Excluir card"
              className={`inline-flex items-center gap-2 px-2.5 py-1 rounded-md border text-xs ${
                deleting
                  ? "bg-gray-100 border-gray-200 text-gray-400 cursor-not-allowed"
                  : "bg-red-50 border-red-200 text-red-700 hover:bg-red-100"
              }`}
            >
              <HiOutlineTrash size={16} />
              {deleting ? "Excluindo..." : "Excluir"}
            </button>
          </div>
        </Modal.Header>

        <Modal.Body>
          {/* Timeline desktop */}
          {stages?.length > 0 && (
            <StageTimeline
              stages={stages || []}
              currentStageId={task?.stageId}
              onClickStage={handleGoToStage}
            />
          )}

          {/* Grid principal */}
          <div className="grid grid-cols-1 lg:grid-cols-6 gap-4">
            {/* ===== ESQ ===== */}
            <div
              className="lg:col-span-2 space-y-6 overflow-y-auto pr-3"
              style={{ maxHeight: "68vh" }}
            >
              {/* === DETALHES DO CLIENTE + TAG === */}
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 relative">
                <div className="absolute right-2 top-2">
                  {!isEditingClient ? (
                    <Button size="xs" color="light" onClick={openClientEditor}>
                      <HiOutlinePencil className="mr-1" /> Editar
                    </Button>
                  ) : null}
                </div>

                {!isEditingClient ? (
                  <>
                    <div className="grid grid-cols-1 gap-3 text-sm">
                      <div>
                        <div className="font-semibold">Documento</div>
                        <span className="text-gray-700 inline-flex items-center gap-2">
                          <span className="text-gray-700">
                            {customerDocument
                              ? formatDocument(customerDocument)
                              : "—"}
                          </span>
                          {customerDocument && (
                            <ReceitaTooltip cnpj={customerDocument} />
                          )}
                        </span>
                      </div>
                      <div>
                        <div className="font-semibold">
                          Faturamento Presumido
                        </div>
                        <span className="text-gray-700 inline-flex items-center gap-2">
                          <span className="text-gray-700">
                            {additionalInfo?.presumedRevenue ||
                              "Sem Informação"}
                          </span>
                        </span>
                      </div>
                      <div>
                        <div className="font-semibold">Estado</div>
                        <span className="text-gray-700 inline-flex items-center gap-2">
                          <span className="text-gray-700">
                            {getStateLabel(additionalInfo?.state) ||
                              "Sem Informação"}
                          </span>
                        </span>
                      </div>
                      <div className="flex flex-col gap-1">
                        <div className="font-semibold">Origem do lead</div>
                        <span className="text-gray-700">
                          {formatLeadSourceLabel(resolveLeadSource(task?.lead))}
                        </span>
                      </div>
                    </div>

                    {leadId && (
                      <div className="mt-6">
                        <LeadContactsPanel leadId={leadId} />
                      </div>
                    )}

                    {showTagPicker && companyIdForTags && (
                      <TagPicker
                        companyId={companyIdForTags}
                        cardId={task?.id}
                        currentTag={currentTag}
                        currentTags={currentTags}
                        onAssigned={handleAssignTags}
                        onClose={() => setShowTagPicker(false)}
                      />
                    )}
                    {showTagPicker && !companyIdForTags && (
                      <div className="text-xs text-red-600 mt-2">
                        Não foi possível identificar a empresa do card para
                        listar etiquetas.
                      </div>
                    )}
                  </>
                ) : (
                  // Edição (DESKTOP)
                  <div className="flex flex-col gap-2 items-end">
                    <div className="grid grid-cols-1 gap-3 flex-1 w-full">
                      <div>
                        <label className="text-xs text-gray-600 block mb-1">
                          Nome
                        </label>
                        <TextInput
                          value={clientName}
                          onChange={(e) => setClientName(e.target.value)}
                        />
                      </div>
                      <div>
                        <label className="text-xs text-gray-600 block mb-1">
                          E-mail
                        </label>
                        <TextInput
                          type="email"
                          value={clientEmail}
                          onChange={(e) => setClientEmail(e.target.value)}
                        />
                      </div>
                      <div>
                        <label className="text-xs text-gray-600 block mb-1">
                          Telefone
                        </label>
                        <TextInput
                          value={clientPhone}
                          onChange={(e) => setClientPhone(e.target.value)}
                          placeholder="(11) 99999-9999"
                        />
                      </div>
                      <div>
                        <label className="text-xs text-gray-600 block mb-1">
                          Documento
                        </label>
                        <TextInput
                          value={clientDocument}
                          onChange={(e) => setClientDocument(e.target.value)}
                        />
                      </div>
                      <div>
                        <label className="text-xs text-gray-600 block mb-1">
                          Faturamento presumido
                        </label>
                        <TextInput
                          value={additionalInfo.presumedRevenue}
                          onChange={(event) =>
                            handlePresumedRevenueInputChange(event.target.value)
                          }
                          onBlur={handlePresumedRevenueBlur}
                          placeholder="R$ 0,00"
                        />
                      </div>
                      <div>
                        <label className="text-xs text-gray-600 block mb-1">
                          Estado
                        </label>
                        <Select
                          value={additionalInfo.state || ""}
                          onChange={(event) =>
                            handleStateChange(event.target.value)
                          }
                          className="bg-white text-gray-900"
                        >
                          <option value="">Selecione um estado</option>
                          {BRAZILIAN_STATES.map((stateOption) => (
                            <option
                              key={stateOption.value}
                              value={stateOption.value}
                            >
                              {stateOption.label}
                            </option>
                          ))}
                        </Select>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        onClick={handleSaveClientDetails}
                        disabled={savingClient}
                      >
                        {savingClient ? "Salvando…" : "Salvar"}
                      </Button>
                      <Button
                        size="sm"
                        color="light"
                        onClick={() => {
                          setIsEditingClient(false);
                          const c = task?.lead || {};
                          const cEmail = c?.email || c?.emails?.[0] || "";
                          setClientName(c?.name || "");
                          setClientEmail(cEmail || "");
                          setClientPhone(c?.phoneNumber || "");
                          setClientDocument(c?.document || "");
                          setClientResponsible(
                            c?.responsibleName || c?.name || ""
                          );
                        }}
                      >
                        Cancelar
                      </Button>
                    </div>
                  </div>
                )}
              </div>

              {/* Valor da venda (desktop) + Fee */}
              {/* <div className="rounded-2xl border border-gray-600 bg-gradient-to-br from-gray-400 via-gray-500 to-gray-600 p-4 shadow-sm text-white">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="grid place-items-center w-8 h-8 rounded-lg bg-white/15 border border-white/20">
                      <HiCurrencyDollar size={18} />
                    </div>
                    <span className="text-xs uppercase tracking-wide opacity-90">
                      Valor da venda
                    </span>
                  </div>
                  {!isEditingDeal && (
                    <button
                      type="button"
                      onClick={onEditDeal}
                      className="inline-flex items-center gap-2 px-2.5 py-1.5 rounded-md bg-white/10 hover:bg-white/20 border border-white/20 text-white text-xs"
                      title="Editar valores"
                    >
                      <HiOutlinePencil size={16} /> Editar
                    </button>
                  )}
                </div>
                {!isEditingDeal ? (
                  <>
                    <div className="mt-2">
                      <div className="flex items-baseline gap-2">
                        <span className="text-2xl md:text-3xl font-semibold leading-none tracking-tight">
                          {dealValue.toLocaleString("pt-BR", {
                            style: "currency",
                            currency: "BRL",
                          })}
                        </span>
                      </div>
                      <p className="mt-1 text-xs opacity-85">
                        Valor previsto/fechado desta oportunidade.
                      </p>
                    </div>

                    <div className="mt-3 text-xs opacity-90 flex items-baseline gap-1 bg-green-200 w-fit px-2 py-1 rounded-xl text-green-800">
                      <span className="uppercase tracking-wide">
                        Fee / taxa
                      </span>
                      <span className="font-semibold">
                        {feeValue.toLocaleString("pt-BR", {
                          style: "currency",
                          currency: "BRL",
                        })}
                      </span>
                    </div>
                  </>
                ) : (
                  <div className="mt-3 space-y-3">
                    <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
                      <div className="flex flex-col flex-1">
                        <Label className="text-white">Valor da venda</Label>
                        <TextInput
                          value={dealInput}
                          onChange={onChangeDeal}
                          placeholder="R$ 0,00"
                          aria-label="Valor da venda"
                          className="bg-white text-gray-900 rounded-md"
                        />
                      </div>
                    </div>
                    <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
                      <div className="flex flex-col flex-1">
                        <Label className="text-white">Valor do fee</Label>
                        <TextInput
                          value={feeInput}
                          onChange={onChangeFee}
                          placeholder="Fee / taxa (R$)"
                          aria-label="Fee / taxa"
                          className="bg-white text-gray-900 rounded-md"
                        />
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button size="sm" color="light" onClick={onSaveDeal}>
                        Salvar
                      </Button>
                      <Button size="sm" color="light" onClick={onCancelDeal}>
                        Cancelar
                      </Button>
                    </div>
                  </div>
                )}
              </div> */}

              <ApprovalValuesPanel {...approvalPanelProps} />

              {/* Responsável do card */}
              <div>
                <h4 className="text-sm font-semibold text-gray-700 mb-1">
                  Responsável
                </h4>
                {isEditingResponsible ? (
                  <div className="flex flex-col gap-2 mt-3">
                    <div className="flex-1">
                      <AutoCompleteInput
                        label="Responsável"
                        name="card-responsible"
                        options={userOptions}
                        value={selectedResponsible || ""}
                        onChange={handleSaveResponsible}
                        isLoading={loadingUsers}
                      />
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center mt-1 rounded-lg bg-gray-50 px-2 py-3 justify-between">
                    <div className="flex items-center gap-2">
                      <Avatar size="sm" rounded />
                      <span className="text-sm text-gray-800 font-medium">
                        {task?.responsible?.name || "Não atribuído"}
                      </span>
                      {selectedResponsible && (
                        <button
                          className="p-0.5 text-sm rounded-full bg-gray-200 "
                          onClick={() => handleSaveResponsible({ value: null })}
                        >
                          <HiOutlineX />
                        </button>
                      )}
                    </div>
                    <div className="flex gap-2 items-center">
                      <button
                        onClick={() => setIsEditingResponsible(true)}
                        className="text-gray-500 hover:text-gray-700"
                      >
                        <HiOutlinePencil size={16} />
                      </button>
                    </div>
                  </div>
                )}
              </div>

              {/* TAG única do card */}
              <div className="mt-6">
                <div className="flex items-center justify-between mb-2">
                  <h5 className="text-sm font-semibold text-gray-800">
                    Etiquetas do card
                  </h5>
                  <Button
                    size="xs"
                    color="light"
                    onClick={() => setShowTagPicker(true)}
                  >
                    Gerenciar etiquetas
                  </Button>
                </div>

                {currentTags.length ? (
                  <div className="flex flex-wrap items-center gap-2">
                    {currentTags.map((tag) => (
                      <span
                        key={tag.id}
                        className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md text-sm font-medium border"
                        style={{
                          background: `${tag.color || "#6b7280"}20`,
                          color: tag.color || "#6b7280",
                          borderColor: tag.color || "#6b7280",
                        }}
                      >
                        <span
                          className="inline-block h-4 w-6 rounded"
                          style={{ background: tag.color || "#6b7280" }}
                        />
                        {tag.name}
                      </span>
                    ))}
                    <Button
                      size="xs"
                      color="light"
                      onClick={() => handleAssignTags([])}
                      title="Remover etiquetas"
                    >
                      Remover todas
                    </Button>
                  </div>
                ) : (
                  <div className="text-xs text-gray-500">
                    Sem etiqueta atribuída.
                  </div>
                )}
              </div>
            </div>

            {/* ===== DIR – TABS CUSTOM (tarefas / adicionar / comentários / formulário) ===== */}
            <div
              className="lg:col-span-4 overflow-y-auto pr-3"
              style={{ maxHeight: "68vh" }}
            >
              <div className="rtabs rounded-xl border border-gray-200 bg-white shadow-sm overflow-hidden">
                {/* Radios para controlar as abas (sem state) */}
                <input
                  type="radio"
                  id="rtab-tarefas"
                  name="rtabs"
                  defaultChecked
                  className="rtab-input"
                />
                <input
                  type="radio"
                  id="rtab-coment"
                  name="rtabs"
                  className="rtab-input"
                />
                <input
                  type="radio"
                  id="rtab-info"
                  name="rtabs"
                  className="rtab-input"
                />

                {/* NAV */}
                <div className="rtabs-nav flex items-center gap-1 px-2 py-2 border-b bg-gray-50 overflow-x-auto no-scrollbar">
                  <label htmlFor="rtab-tarefas" className="rtab-btn">
                    <span className="rtab-dot" /> Tarefas
                    <span className="rtab-badge">{tasks?.length || 0}</span>
                  </label>
                  <label htmlFor="rtab-coment" className="rtab-btn">
                    <span className="rtab-dot" /> Comentários
                    <span className="rtab-badge">
                      {commentOnlyHistory.length}
                    </span>
                  </label>
                  <label htmlFor="rtab-info" className="rtab-btn">
                    <span className="rtab-dot" /> Informações adicionais
                  </label>
                </div>

                {/* PANELS */}
                <div className="rtabs-panels p-3">
                  <div className="rtab-panel space-y-4">
                    {!isEditingTask && (
                      <div className="flex justify-end">
                        <Button
                          size="sm"
                          onClick={() => {
                            setIsEditingTask(true);
                          }}
                        >
                          Adicionar tarefa
                        </Button>
                      </div>
                    )}

                    {isEditingTask && (
                      <div>
                        <div className="flex flex-wrap gap-1.5">
                          {ACTION_PRESETS.map(({ key, label, icon: Icon }) => {
                            const active = actionType === key;
                            return (
                              <button
                                key={key}
                                type="button"
                                onClick={() => {
                                  setActionType(key);
                                  setActionNoteHtml(defaultNoteFor(key));
                                }}
                                className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-full border text-xs transition-all ${
                                  active
                                    ? "bg-gray-900 border-gray-900 text-white"
                                    : "bg-white border-gray-300 text-gray-700 hover:bg-gray-50"
                                }`}
                              >
                                <Icon size={14} />
                                <span>{label}</span>
                              </button>
                            );
                          })}
                        </div>

                        <div className="mt-3">
                          <label className="text-sm text-gray-700 font-medium mb-1 block">
                            Descrição
                          </label>
                          <TiptapEditor
                            value={actionNoteHtml}
                            onChange={setActionNoteHtml}
                            users={(availableUsers || []).map((u) => ({
                              id: u.id,
                              value: u.name || u.email || "Usuário",
                              email: u.email,
                              avatar: u.avatarUrl,
                            }))}
                            placeholder="Detalhe a ação (opcional)"
                          />
                          <div className="mt-2 flex justify-end">
                            <DictationButton target="action" />
                          </div>
                        </div>

                        <AttachmentPicker
                          label="Anexos"
                          files={actionFiles}
                          onChange={setActionFiles}
                          accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt"
                        />

                        <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3">
                          <div>
                            <label className="text-sm text-gray-700 font-medium mb-1 block">
                              Data do prazo
                            </label>
                            <TextInput
                              type="date"
                              value={actionDueDate}
                              onChange={(e) => setActionDueDate(e.target.value)}
                            />
                          </div>
                          <div>
                            <label className="text-sm text-gray-700 font-medium mb-1 block">
                              Horário do prazo
                            </label>
                            <input
                              type="time"
                              value={actionDueTime || ""}
                              onChange={(e) => setActionDueTime(e.target.value)}
                              step="900"
                              min="08:00"
                              max="20:00"
                              className="w-full border rounded-md p-2 text-sm"
                            />
                          </div>

                          {actionType === "whatsapp" && (
                            <div className="sm:col-span-2">
                              <label className="text-sm text-gray-700 font-medium mb-1 block">
                                Telefone (opcional p/ abrir WhatsApp)
                              </label>
                              <TextInput
                                type="tel"
                                placeholder="DDD + número"
                                value={actionPhone}
                                onChange={(e) => setActionPhone(e.target.value)}
                              />
                            </div>
                          )}
                        </div>

                        <div className="mt-5">
                          <AutoCompleteInput
                            label="Responsáveis da tarefa"
                            name="task-responsible"
                            options={userOptions}
                            value={actionResponsibleIds}
                            isMulti
                            onChange={(opts) =>
                              setActionResponsibleIds(
                                (opts || [])
                                  .map((opt) => opt?.value)
                                  .filter(Boolean)
                              )
                            }
                            isLoading={loadingUsers}
                          />
                        </div>

                        <div className="mt-4 flex flex-wrap gap-3">
                          <Button
                            size="sm"
                            onClick={handleSaveNextAction}
                            disabled={isUploadingTask}
                            aria-busy={isUploadingTask}
                          >
                            {isUploadingTask
                              ? uploadPct
                                ? `Enviando… ${uploadPct}%`
                                : "Enviando…"
                              : "Salvar tarefa"}
                          </Button>

                          {actionType === "whatsapp" && actionPhone && (
                            <Button
                              size="sm"
                              color="success"
                              onClick={() => {
                                const msg = encodeURIComponent(
                                  (actionNoteHtml || "")
                                    .replace(/<[^>]+>/g, " ")
                                    .replace(/\s+/g, " ")
                                    .trim()
                                );
                                const phone = actionPhone.replace(/\D/g, "");
                                window.open(
                                  `https://wa.me/${phone}?text=${msg}`,
                                  "_blank"
                                );
                              }}
                            >
                              Abrir WhatsApp
                            </Button>
                          )}
                        </div>
                      </div>
                    )}

                    <div className="border-t pt-4">
                      <TaskListCompact
                        tasks={tasks}
                        loadingTasks={loadingTasks}
                        labelMap={labelMap}
                        getUserNameById={getUserNameById}
                        startEditTask={startEditTask}
                        handleToggleTaskStatus={handleToggleTaskStatus}
                        handleDeleteTask={handleDeleteTask}
                        menuBtnRef={menuBtnRef}
                        openMenuId={openMenuId}
                        setOpenMenuId={setOpenMenuId}
                        DropdownPortal={DropdownPortal}
                        onConfirmComplete={openConfirmComplete}
                      />
                    </div>
                  </div>

                  <div className="rtab-panel">
                    <div className="rounded-2xl overflow-hidden -mt-3">
                      <div className="p-2">
                        <div className=" overflow-hidden">
                          <TiptapEditor
                            value={commentHtml}
                            onChange={setCommentHtml}
                            users={(availableUsers || []).map((u) => ({
                              id: u.id,
                              value: u.name || u.email || "Usuário",
                              email: u.email,
                              avatar: u.avatarUrl,
                            }))}
                            placeholder="Escreva seu comentário…"
                            className="comment-editor"
                            minHeight={50}
                          />
                          <div className="px-1.5 py-2.5 bg-white/70 flex items-center justify-between gap-2">
                            <DictationButton target="comment" />
                            <div className="flex items-center gap-2">
                              <Button
                                size="sm"
                                color="light"
                                onClick={() =>
                                  window.confirm("Limpar texto?") &&
                                  setCommentHtml("")
                                }
                              >
                                Limpar
                              </Button>
                              <Button size="sm" onClick={handleAddComment}>
                                Enviar
                              </Button>
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="p-1 pt-3 pr-2 rounded-md ">
                        {commentOnlyHistory.length > 0 ? (
                          <div className="max-h-[340px] overflow-y-auto soft-scroll px-2">
                            <ul className="space-y-2">
                              {commentOnlyHistory.map((h) => {
                                const name =
                                  h.User?.name ||
                                  getUserNameById?.(h.userId || h.user_id) ||
                                  "Não atribuído";
                                const initials = name
                                  .split(" ")
                                  .map((s) => s[0])
                                  .slice(0, 2)
                                  .join("")
                                  .toUpperCase();
                                const ts = new Date(h.createdAt).toLocaleString(
                                  "pt-BR"
                                );
                                const edited = !!(h.editedAt || h.isEdited);
                                return (
                                  <li
                                    key={h.id}
                                    className="group border-b border-gray-200 bg-white p-2 transition-shadow  relative"
                                  >
                                    <div className="flex items-center gap-3 min-w-0">
                                      <div className="w-8 h-8 rounded-full bg-gray-100 text-gray-700 grid place-items-center text-[11px] font-semibold shrink-0">
                                        {initials}
                                      </div>
                                      <div className="min-w-0">
                                        <div className="flex items-center gap-2">
                                          <span className="text-sm font-medium text-gray-900 truncate">
                                            {name}
                                          </span>
                                          <span className="hidden sm:inline text-[11px] text-gray-400">
                                            •
                                          </span>
                                          <span className="hidden sm:inline text-[11px] text-gray-500">
                                            {ts}{" "}
                                            {edited && (
                                              <span className="ml-1 text-gray-400">
                                                (editado)
                                              </span>
                                            )}
                                          </span>
                                        </div>
                                        <div className="sm:hidden text-[11px] text-gray-500">
                                          {ts}{" "}
                                          {edited && (
                                            <span className="ml-1 text-gray-400">
                                              (editado)
                                            </span>
                                          )}
                                        </div>
                                      </div>
                                    </div>

                                    {editingCommentId === h.id ? (
                                      <TiptapEditor
                                        value={editCommentHtml}
                                        onChange={setEditCommentHtml}
                                        users={(availableUsers || []).map(
                                          (u) => ({
                                            id: u.id,
                                            value:
                                              u.name || u.email || "Usuário",
                                            email: u.email,
                                            avatar: u.avatarUrl,
                                          })
                                        )}
                                        placeholder="Edite seu comentário…"
                                      />
                                    ) : (
                                      <div
                                        className="rich-text max-w-none mt-2 text-gray-800 text-sm"
                                        dangerouslySetInnerHTML={{
                                          __html: h.text || "",
                                        }}
                                      />
                                    )}

                                    {isCommentOwner(h) && (
                                      <div className="absolute top-4 right-4 flex justify-end gap-3">
                                        {editingCommentId !== h.id ? (
                                          <>
                                            <button
                                              className="text-[16px] text-blue-600 underline"
                                              onClick={() => {
                                                setEditingCommentId(h.id);
                                                setEditCommentHtml(
                                                  h.text || ""
                                                );
                                              }}
                                            >
                                              <MdOutlineModeEdit />
                                            </button>
                                            <button
                                              className="text-[12px] text-red-600 underline"
                                              onClick={() =>
                                                handleDeleteComment(h)
                                              }
                                            >
                                              <FaRegTrashCan />
                                            </button>
                                          </>
                                        ) : (
                                          <>
                                            <button
                                              className="text-[12px] text-gray-700 underline"
                                              onClick={() => {
                                                setEditingCommentId(null);
                                                setEditCommentHtml("");
                                              }}
                                            >
                                              Cancelar
                                            </button>
                                            <button
                                              className="text-[12px] text-blue-600 underline"
                                              onClick={() =>
                                                handleSaveEditedComment(h)
                                              }
                                            >
                                              Salvar
                                            </button>
                                          </>
                                        )}
                                      </div>
                                    )}
                                  </li>
                                );
                              })}
                            </ul>
                          </div>
                        ) : (
                          <div className="text-center py-10 text-sm text-gray-600 flex flex-col items-center justify-center">
                            <span className="text-3xl">💬</span>
                            <span>Ainda não há comentários.</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="rtab-panel">
                    <div className="space-y-4">
                      {renderAdditionalInfoInputs()}
                      <div className="flex flex-wrap gap-2">
                        <Button
                          size="sm"
                          onClick={handleSaveAdditionalInfo}
                          disabled={savingAdditionalInfo}
                        >
                          {savingAdditionalInfo ? "Salvando…" : "Salvar"}
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Modal.Body>

        {/* Modal de edição de tarefa existente */}
        <TaskEditModal
          open={editModalOpen}
          onClose={() => {
            setEditModalOpen(false);
          }}
          taskBeingEdited={taskBeingEdited}
          editingData={{
            editType,
            editNoteHtml,
            editDueDate,
            editDueTime,
            editResponsibleIds,
          }}
          setEditType={setEditType}
          setEditNoteHtml={setEditNoteHtml}
          setEditDueDate={setEditDueDate}
          setEditDueTime={setEditDueTime}
          setEditResponsibleIds={setEditResponsibleIds}
          userOptions={userOptions}
          loadingUsers={loadingUsers}
          saveEditTask={saveEditTask}
          cancelEditTask={cancelEditTask}
          onDeleteAttachment={handleDeleteAttachment}
          editFiles={editFiles}
          setEditFiles={setEditFiles}
          labelMap={labelMap}
          prettySize={prettySize}
          fileIconFor={fileIconFor}
        />

        <Modal show={!!confirmCompleteTask} onClose={closeConfirmComplete}>
          <Modal.Header>Concluir tarefa</Modal.Header>
          <Modal.Body>
            <div className="text-sm text-gray-700">
              Tem certeza de que deseja marcar esta tarefa como <b>concluída</b>
              ?
            </div>
            {confirmCompleteTask?.note && (
              <div className="mt-2 p-2 rounded bg-gray-50 border text-xs text-gray-600">
                <div className="font-medium mb-1">Resumo:</div>
                <div
                  className="prose prose-sm text-gray-700 max-w-none"
                  dangerouslySetInnerHTML={{ __html: confirmCompleteTask.note }}
                />
              </div>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button size="sm" onClick={confirmCompleteNow}>
              Concluir
            </Button>
            <Button size="sm" color="light" onClick={closeConfirmComplete}>
              Cancelar
            </Button>
          </Modal.Footer>
        </Modal>
      </div>

      {/* util styles */}
      <style>{`
        .no-scrollbar::-webkit-scrollbar{ display:none; }
        .no-scrollbar{ -ms-overflow-style:none; scrollbar-width:none; }
      `}</style>
      <style>{`
  .rich-text{ line-height:1.55; }
  .rich-text p{ margin:.3rem 0; min-height:1.55em; }
  .rich-text ul,.rich-text ol{ margin:.4rem 0 .4rem 0; padding-left:1.25rem; }
  .rich-text ul{ list-style: disc outside; }
  .rich-text ol{ list-style: decimal outside; }
  .rich-text li{ margin:.2rem 0; }
  .rich-text h1{ font-size:1.45rem; font-weight:700; margin:.5rem 0 .6rem; }
  .rich-text h2{ font-size:1.25rem; font-weight:700; margin:.5rem 0 .55rem; }
  .rich-text h3{ font-size:1.1rem; font-weight:700; margin:.45rem 0 .5rem; }
  .rich-text blockquote{ border-left:3px solid #e5e7eb; margin:.6rem 0; padding:.35rem .8rem; color:#4b5563; background:#f9fafb; border-radius:6px; }
  .rich-text a{ text-decoration: underline; color:#1d4ed8; }
  .rich-text code{ background:#f3f4f6; border:1px solid #e5e7eb; border-radius:6px; padding:1px 6px; font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono","Courier New", monospace; font-size:.92em; }
  .rich-text pre{ background:#0b1020; color:#f3f4f6; border-radius:10px; padding:10px 12px; overflow:auto; border:1px solid #111827; }
  .rich-text pre code{ background:transparent; border:0; padding:0; color:inherit; font-size:.95em; }

  /* ====== Custom Right Tabs ====== */
  .rtab-input{ display:none; }
  .rtabs-nav .rtab-btn{
    position: relative;
    display:inline-flex; align-items:center; gap:.5rem;
    padding:.5rem .75rem; border-radius:.6rem;
    font-size:.875rem; font-weight:600; color:#4b5563; cursor:pointer;
    border:1px solid transparent;
    transition: background .2s, color .2s, border-color .2s, box-shadow .2s;
    white-space: nowrap;
  }
  .rtabs-nav .rtab-btn:hover{ background:#fff; border-color:#e5e7eb; color:#111827; }
  .rtab-dot{
    width:.5rem; height:.5rem; border-radius:9999px; background:#9ca3af; display:inline-block;
  }
  .rtab-badge{
    display:inline-flex; align-items:center; justify-content:center;
    padding:0 .4rem; height:1.1rem; border-radius:.6rem;
    font-size:.70rem; font-weight:700; background:#eef2ff; color:#4f46e5; border:1px solid #e0e7ff;
  }
  /* States */
  #rtab-tarefas:checked ~ .rtabs-nav label[for="rtab-tarefas"],
  #rtab-coment:checked ~ .rtabs-nav label[for="rtab-coment"],
  #rtab-info:checked ~ .rtabs-nav label[for="rtab-info"]{
    background:#111827; color:#fff; border-color:#111827;
    box-shadow: 0 1px 0 rgba(17,24,39,.1), 0 1px 2px rgba(17,24,39,.08) inset;
  }
  #rtab-tarefas:checked ~ .rtabs-nav label[for="rtab-tarefas"] .rtab-dot,
  #rtab-coment:checked ~ .rtabs-nav label[for="rtab-coment"] .rtab-dot,
  #rtab-info:checked ~ .rtabs-nav label[for="rtab-info"] .rtab-dot{ background:#fff; }

  .rtabs-panels .rtab-panel{ display:none; }
  #rtab-tarefas:checked ~ .rtabs-panels .rtab-panel:nth-of-type(1){ display:block; }
  #rtab-coment:checked  ~ .rtabs-panels .rtab-panel:nth-of-type(2){ display:block; }
  #rtab-info:checked    ~ .rtabs-panels .rtab-panel:nth-of-type(3){ display:block; }
`}</style>
    </Modal>
  );
}
